/**
 * BLACKOUT: KHALIJ — a sun-bleached Levantine coastal town block.
 *
 * Classic three-lane competitive layout:
 *   LEFT   a narrow shaded alley of arches, with stairs to a balcony walkway,
 *          a plank bridge and a flat roof terrace overlooking the square.
 *   CENTRE an open market square: stalls, awnings, a dry fountain, two dead
 *          vehicles as hard cover.
 *   RIGHT  a two-storey souk house you fight through — shopfront, back room,
 *          staircase, first floor, roof access — with windows cut deliberately
 *          onto the square.
 * Cross-streets at Z = ±19 tie the lanes together; spawns sit at Z = ±30.
 *
 * owner: level
 */
import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { makeRng } from '../core/Rand.js';
import { chamferBox, chamferCyl, quadXZ, xform, alignX, taperCyl, tube, stripAttrs } from './Geo.js';
import { World } from './kit/World.js';
import { wall, slab, damagePatch } from './kit/Walls.js';
import { archway, colonnade } from './kit/Arch.js';
import { stairs, railing, balcony, ladder } from './kit/Stairs.js';
import { blockBuilding, windowRow, powerRun } from './kit/Building.js';
import {
  groundPatch, pavement, kerbRun, manhole, crackScatter, roadLine, step,
  jerseyBarrier, bollards, driftPatch, groundBlend, pebbleScatter, stainScatter,
  tyreTracks,
} from './kit/Street.js';
import {
  roofClutter, acUnit, satDish, antenna, waterTank, wire, wireBundle, laundryLine,
  drainpipe, conduit, awning, sign, hangingBulb, wallLamp, shrub, rebar, chimneyStack,
  rooflineClutter, latticeTower, cablePole, linkCables, whipMast, dishMast, parapetSign,
  laundryPost, ACCENT,
} from './kit/Detail.js';
import {
  defineProps, sandbagWall, rubblePile, weeds, scatter, sedan, pickup,
  hangingFabric, variantTint, accentTint,
} from './Props.js';

/* ------------------------------------------------------------------ *
 * material resolution — code against the frozen ctx.materials API, but
 * stay legible while the materials team is still building theirs.
 * ------------------------------------------------------------------ */

const SURFACE_OF = {
  concrete_floor: 'concrete', concrete_wall: 'concrete', concrete_wall_painted: 'concrete',
  plaster_wall: 'concrete', brick_wall: 'concrete', stucco: 'concrete', tile_ceramic: 'concrete',
  asphalt: 'concrete', gravel: 'dirt', dirt: 'dirt', sand: 'sand',
  metal_painted: 'metal', metal_rusted: 'metal', metal_grate: 'metal',
  corrugated_metal: 'metal', steel_brushed: 'metal',
  wood_plank: 'wood', wood_crate: 'wood', wood_painted: 'wood', plywood: 'wood',
  fabric_tarp: 'fabric', sandbag: 'fabric', canvas: 'fabric',
  glass: 'glass', glass_dirty: 'glass', rubber: 'rubber', plastic_crate: 'plastic',
  foliage: 'foliage',
};

/** Sun-bleached Levantine palette, used only while a material is still the grey stub. */
const FALLBACK = {
  concrete_floor: [0x9d968a, 0.92, 0], concrete_wall: [0xaaa397, 0.9, 0],
  concrete_wall_painted: [0xcac2b0, 0.85, 0], plaster_wall: [0xd9cdb4, 0.88, 0],
  brick_wall: [0x9b6a4c, 0.95, 0], stucco: [0xded3bb, 0.86, 0],
  tile_ceramic: [0xb87b55, 0.55, 0], asphalt: [0x4d4a46, 0.96, 0],
  sand: [0xc9b58b, 0.98, 0], gravel: [0x8f8878, 0.98, 0], dirt: [0x7c6a52, 1.0, 0],
  metal_painted: [0x8d9089, 0.55, 0.75], metal_rusted: [0x7a5340, 0.82, 0.6],
  metal_grate: [0x57585a, 0.62, 0.85], corrugated_metal: [0x8b908c, 0.6, 0.8],
  steel_brushed: [0xa2a5a7, 0.42, 0.9],
  wood_plank: [0x8b6b46, 0.9, 0], wood_crate: [0xa37a4c, 0.88, 0],
  wood_painted: [0x6f7f79, 0.8, 0], plywood: [0xb69b6f, 0.9, 0],
  fabric_tarp: [0xc0af90, 0.95, 0], sandbag: [0xb5a378, 0.98, 0], canvas: [0xc9b993, 0.95, 0],
  glass: [0x2b3a44, 0.14, 0.45], glass_dirty: [0x36423f, 0.28, 0.35],
  rubber: [0x2d2d2f, 0.95, 0], plastic_crate: [0x3d6ba4, 0.55, 0],
  foliage: [0x6d7c46, 0.92, 0],
};

/**
 * Materials whose level geometry is authored as two-sided *geometry*
 * (Geo.twoSided). Their material must stay FrontSide — see makeResolver.
 */
const TWO_SIDED_GEO = new Set(['foliage', 'canvas', 'fabric_tarp', 'sandbag', 'glass', 'glass_dirty']);

function makeResolver(ctx) {
  const cache = new Map();
  return (name) => {
    if (cache.has(name)) return cache.get(name);
    let m = null;
    try {
      if (ctx.materials?.make) m = ctx.materials.make(name);
      else if (ctx.materials?.get) m = ctx.materials.get(name)?.clone();
    } catch (err) { m = null; }
    if (!m) m = new THREE.MeshStandardMaterial({ color: 0x8a8a8a, roughness: 0.92 });
    m.name = `level:${name}`;
    m.vertexColors = true;
    m.userData = { ...(m.userData || {}) };
    if (!m.userData.surface) m.userData.surface = SURFACE_OF[name] || 'concrete';
    const stub = !m.map && m.color && (m.color.getHex() === 0x8a8a8a || m.color.getHex() === 0xffffff);
    const f = FALLBACK[name];
    if (stub && f) {
      m.color.setHex(f[0]);
      if ('roughness' in m) m.roughness = f[1];
      if ('metalness' in m) m.metalness = f[2];
    }
    if (m.alphaMap || (m.map && m.transparent)) m.alphaTest = 0.5;
    /*
     * Every thin card we author — leaf blades, cloth panels, hanging laundry —
     * is already two-sided *geometry* (Geo.twoSided appends a mirrored copy
     * with flipped normals). Forcing THREE.DoubleSide on top of that renders
     * each card FOUR times: front and back of two coincident, oppositely
     * wound triangles. That is what made foliage glow at grazing angles — the
     * back face of the mirror shares the pixel with the front face of the
     * original and their contributions add — and it z-fought as a bonus. The
     * geometry already handles both sides, so the material must not.
     */
    if (TWO_SIDED_GEO.has(name)) m.side = THREE.FrontSide;
    if (name === 'foliage') {
      // dry dusty leaves are not satin: keep the sheen lobe small so the
      // remaining grazing-angle response stays a rim, not a halo
      if ('sheen' in m) m.sheen = Math.min(m.sheen ?? 0, 0.10);
      if (m.sheenColor) m.sheenColor.setRGB(0.35, 0.38, 0.22);
    }
    m.shadowSide = THREE.FrontSide;
    cache.set(name, m);
    return m;
  };
}

/* ------------------------------------------------------------------ *
 * map constants
 * ------------------------------------------------------------------ */

/*
 * Cloth palettes. These multiply the `canvas` albedo, which is a raw-duck
 * 0.05–0.14 linear — far too dark for dyed market cloth on its own, and the
 * reason every awning read as a black slab while it was still on the even
 * darker `fabric_tarp`. AO_PROFILES.canvas raises the vertex-colour ceiling to
 * 2.8 so these get through instead of being clipped at 1.2.
 */
const AWNING = [
  [2.35, 1.55, 1.05],   // faded terracotta stripe
  [1.60, 1.85, 2.30],   // washed blue
  [2.40, 2.15, 1.55],   // bleached cream
  [2.10, 1.75, 0.95],   // ochre
  [1.35, 1.80, 1.45],   // sun-rotted green
  [2.30, 1.30, 1.20],   // red
];
const RUG = [
  [2.20, 1.15, 0.90],   // madder red
  [1.35, 1.45, 1.95],   // indigo
  [2.15, 1.80, 1.05],   // saffron
];

const PLAY = { x0: -34, x1: 34, z0: -34, z1: 34 };
const ALLEY = { x0: -26, x1: -20 };            // left lane corridor
const SQUARE = { x0: -13.5, x1: 15, z0: -17, z1: 17 };
const SHOP = { x0: 15, x1: 29, z0: -14, z1: 14, f1: 3.55, top: 7.1, para: 1.05 };
const BLOCKB = { x0: -20, x1: -13.5, z0: -26, z1: 16, roof: 4.3, wallH: 4.0 };
const PASS = { z0: -6.2, z1: -2.2 };            // through-passage in block B

/* ------------------------------------------------------------------ *
 * the build
 * ------------------------------------------------------------------ */

function buildGround(w) {
  // base terrain well past the ring so the horizon never shows an edge.
  // The outermost shell is scenery only; the two inner shells are the floor of
  // last resort so nothing can ever fall out of the world.
  groundPatch(w, { x0: -520, z0: -520, x1: 520, z1: 520, y: -0.09, mat: 'dirt', surface: 'dirt', res: 60, uvScale: 22, collide: false });
  groundPatch(w, { x0: -130, z0: -130, x1: 130, z1: 130, y: -0.06, mat: 'dirt', surface: 'dirt', res: 8, uvScale: 8 });
  groundPatch(w, { x0: -50, z0: -50, x1: 50, z1: 50, y: -0.02, mat: 'sand', surface: 'sand', res: 2.2, uvScale: 4 });

  // market square paving
  groundPatch(w, { x0: SQUARE.x0, z0: SQUARE.z0, x1: SQUARE.x1, z1: SQUARE.z1, y: 0, mat: 'concrete_floor', surface: 'concrete', res: 0.75, uvScale: 1.9 });
  // wind-blown sand drifts over the paving
  const rng = w.rng;
  /*
   * Wind-blown sand over the paving. These were reading as bright hard-edged
   * yellow blobs stuck to the ground — a 0.80 tint on a sand albedo is far
   * lighter than the concrete it sits on, and a ragged rim does not save a patch
   * whose VALUE is wrong. Darker, smaller, and with a second smaller drift
   * feathering each one so the boundary is not a single silhouette.
   */
  for (let i = 0; i < 11; i++) {
    const cx = rng.range(SQUARE.x0 + 1.5, SQUARE.x1 - 1.5);
    const cz = rng.range(SQUARE.z0 + 1.5, SQUARE.z1 - 1.5);
    const rx = rng.range(0.9, 2.1), rz = rng.range(0.7, 1.6), rot = rng.range(0, Math.PI);
    driftPatch(w, { x: cx, z: cz, rx, rz, rot, y: 0.009, seg: 26, tint: [0.60, 0.58, 0.55] });
    driftPatch(w, {
      x: cx + rng.range(-0.7, 0.7), z: cz + rng.range(-0.7, 0.7),
      rx: rx * rng.range(0.4, 0.7), rz: rz * rng.range(0.4, 0.7), rot: rot + rng.range(-0.5, 0.5),
      y: 0.012, seg: 22, tint: [0.72, 0.69, 0.64],
    });
  }
  for (let i = 0; i < 10; i++) {
    driftPatch(w, {
      x: rng.range(ALLEY.x0 + 0.6, ALLEY.x1 - 0.6), z: rng.range(-30, 30),
      rx: rng.range(0.8, 2.0), rz: rng.range(1.4, 4.0), rot: rng.range(0, Math.PI),
      y: 0.011, mat: 'dirt', surface: 'dirt', tint: [0.9, 0.88, 0.86],
    });
  }

  // alley — packed gravel
  groundPatch(w, { x0: ALLEY.x0, z0: -34, x1: ALLEY.x1, z1: 34, y: 0.005, mat: 'gravel', surface: 'dirt', res: 0.7, uvScale: 1.5 });

  // cross streets + spawn courtyards — asphalt
  for (const [z0, z1] of [[-23, -16.6], [16.6, 23]]) {
    groundPatch(w, { x0: -27, z0, x1: 31, z1, y: 0.004, mat: 'asphalt', surface: 'concrete', res: 0.9, uvScale: 2.4, tint: [1.25, 1.15, 0.95] });
    roadLine(w, { a: [-26, (z0 + z1) / 2], b: [30, (z0 + z1) / 2], dash: 1.1, width: 0.13 });
    crackScatter(w, { x0: -26, z0, x1: 30, z1, count: 14 });
  }
  for (const [z0, z1] of [[-34, -23], [23, 34]]) {
    groundPatch(w, { x0: -28, z0, x1: 32, z1, y: 0.003, mat: 'asphalt', surface: 'concrete', res: 1.1, uvScale: 2.6, tint: [1.25, 1.15, 0.95] });
    crackScatter(w, { x0: -27, z0: z0 + 1, x1: 31, z1: z1 - 1, count: 10 });
  }

  // pavements INSIDE the square, hugging the buildings that frame it
  pavement(w, { x0: SQUARE.x0, z0: SQUARE.z0 + 1.6, x1: SQUARE.x0 + 2.1, z1: SQUARE.z1 - 1.6, h: 0.15, kerbs: ['e'] });
  pavement(w, { x0: SQUARE.x1 - 2.2, z0: SQUARE.z0 + 1.6, x1: SQUARE.x1, z1: SQUARE.z1 - 1.6, h: 0.15, kerbs: ['w'] });
  pavement(w, { x0: SQUARE.x0, z0: SQUARE.z0, x1: SQUARE.x1, z1: SQUARE.z0 + 1.6, h: 0.15, kerbs: ['s'] });
  pavement(w, { x0: SQUARE.x0, z0: SQUARE.z1 - 1.6, x1: SQUARE.x1, z1: SQUARE.z1, h: 0.15, kerbs: ['n'] });

  // Tonal variation across the paving. A 28 x 34 m flat with one material on it
  // reads as printed vinyl however good the texture is; these are the patch
  // repairs, oil stains and sun-bleached traffic lanes that break it up.
  for (let i = 0; i < 11; i++) {
    const bleach = rng() < 0.5;
    driftPatch(w, {
      x: rng.range(SQUARE.x0 + 2, SQUARE.x1 - 2), z: rng.range(SQUARE.z0 + 2, SQUARE.z1 - 2),
      rx: rng.range(1.6, 4.4), rz: rng.range(1.4, 3.6), rot: rng.range(0, Math.PI),
      y: 0.006, mat: 'concrete_floor', surface: 'concrete', seg: 26,
      tint: bleach
        ? [rng.range(1.10, 1.24), rng.range(1.08, 1.20), rng.range(1.02, 1.14)]
        : [rng.range(0.66, 0.82), rng.range(0.66, 0.80), rng.range(0.68, 0.82)],
    });
  }
  // asphalt patch repairs where the paving was dug up
  for (let i = 0; i < 4; i++) {
    const pw = rng.range(1.6, 3.4), pd = rng.range(1.2, 2.6);
    const g = quadXZ(pw, pd);
    w.batch.add(g, 'asphalt', {
      matrix: xform([rng.range(SQUARE.x0 + 2.5, SQUARE.x1 - 2.5), 0.007, rng.range(SQUARE.z0 + 2.5, SQUARE.z1 - 2.5)], rng.range(-0.3, 0.3)),
      surface: 'concrete', uvScale: 1.4, cast: false,
      tint: [rng.range(0.9, 1.1), rng.range(0.88, 1.06), rng.range(0.84, 1.0)],
    });
    g.dispose();
  }

  crackScatter(w, { x0: SQUARE.x0 + 1, z0: SQUARE.z0 + 1, x1: SQUARE.x1 - 1, z1: SQUARE.z1 - 1, count: 26, mat: 'concrete_floor' });
  crackScatter(w, { x0: ALLEY.x0, z0: -30, x1: ALLEY.x1, z1: 30, count: 14, mat: 'gravel' });

  manhole(w, { x: 3.2, z: 6.4 });
  manhole(w, { x: -7.6, z: -11.0, grate: true });
  manhole(w, { x: 20.5, z: 19.5 });
  manhole(w, { x: -22.8, z: 8.0, grate: true });

  /* ---- killing the ~300 px ground tile -------------------------------- *
   *
   * The square paving is authored at `uvScale: 1.9`, i.e. a 1.9 m repeat, which
   * at eye height and 74° FOV lands at ~300 px in the lower third of the frame.
   * That is the tile the critic found, and one uniform surface cannot escape its
   * own period however good the texture is.
   *
   * Three superimposed layers at incommensurate pitches (see `BREAK_PITCH` in
   * Street.js), ragged-edged so there is no repeating boundary either, plus
   * aperiodic dark shapes (stains, tracks) and real 3-D loose stone that throws
   * its own micro-shadows. All of it merges into groups that already exist.
   */
  const SQ = { x0: SQUARE.x0 + 0.4, z0: SQUARE.z0 + 0.4, x1: SQUARE.x1 - 0.4, z1: SQUARE.z1 - 0.4 };
  groundBlend(w, {
    ...SQ, y: 0.0072, host: 'concrete_floor', guests: ['gravel', 'asphalt'],
    phase: 15, wear: 26, phaseR: [2.4, 6.6], wearR: [0.5, 2.0], wearValue: [0.84, 1.12],
  });
  stainScatter(w, { ...SQ, y: 0.0098, count: 9, mat: 'concrete_floor', dark: [0.58, 0.86] });
  tyreTracks(w, { a: [SQUARE.x0 + 1, -15.6], b: [SQUARE.x1 - 2, 12.4], gauge: 1.7, y: 0.0094, mat: 'concrete_floor', tint: [0.80, 0.79, 0.77] });
  tyreTracks(w, { a: [8.4, SQUARE.z1 - 1], b: [-4.2, SQUARE.z0 + 2], gauge: 1.55, y: 0.0096, mat: 'concrete_floor', tint: [0.84, 0.83, 0.81] });
  pebbleScatter(w, { ...SQ, y: 0.012, count: 130, size: [0.028, 0.085] });

  /*
   * The host material has to be the one that is ACTUALLY on top at that spot.
   *
   * The first version of this blended `sand` across the outer band on the theory
   * that the map sits in a sand ring. It does not: the courtyards and cross
   * streets are `asphalt` (z ±16.6…34 over x −28…32) and the left lane is
   * `gravel`, so what landed was big bright tan discs on dark tarmac — a
   * two-tone patchwork that was a louder defect than the tile it was curing.
   * The exposed sand ring is behind the ring buildings and is never in frame.
   *
   * Blending a surface with itself at a different pitch is invisible. Blending
   * it with a different albedo is a paint job. Check what is underneath first.
   */

  // alley gravel: a 6 m wide corridor you walk down, so the tile is at its most
  // readable there — and it is the whole ground of `level_alley`
  groundBlend(w, {
    x0: ALLEY.x0 + 0.2, z0: -33, x1: ALLEY.x1 - 0.2, z1: 33, y: 0.0082,
    host: 'gravel', guests: ['dirt'],
    phase: 16, wear: 22, phaseR: [1.2, 3.6], wearR: [0.4, 1.5], wearValue: [0.86, 1.12],
  });
  pebbleScatter(w, { x0: ALLEY.x0 + 0.25, z0: -33, x1: ALLEY.x1 - 0.25, z1: 33, y: 0.012, count: 120, size: [0.03, 0.11] });

  // asphalt cross streets and spawn courtyards — the ground of `wpn_rifle_hip`,
  // `light_goldenhour` and every spawn shot
  for (const [z0, z1] of [[-23, -16.6], [16.6, 23], [-34, -23], [23, 34]]) {
    groundBlend(w, {
      x0: -27, z0, x1: 31, z1, y: 0.0054, host: 'asphalt', guests: ['gravel'],
      phase: 16, wear: 20, phaseR: [2.2, 6.4], wearR: [0.6, 2.2], wearValue: [0.86, 1.14],
      guestChance: 0.20,
    });
    stainScatter(w, { x0: -25, z0: z0 + 1, x1: 29, z1: z1 - 1, y: 0.0078, count: 6, mat: 'asphalt', dark: [0.62, 0.88] });
    pebbleScatter(w, { x0: -26, z0, x1: 30, z1, y: 0.01, count: 90, size: [0.03, 0.10] });
    // a light dusting of wind-blown sand, small and LOW in value: on tarmac a
    // sand drift is a thin film, not a beach
    for (let i = 0; i < 5; i++) {
      driftPatch(w, {
        x: rng.range(-25, 29), z: rng.range(z0 + 1, z1 - 1),
        rx: rng.range(0.7, 2.2), rz: rng.range(0.6, 1.7), rot: rng.range(0, Math.PI),
        y: 0.0094, mat: 'sand', surface: 'sand', seg: 24, uvScale: 3.37,
        tint: [rng.range(0.50, 0.66), rng.range(0.49, 0.64), rng.range(0.47, 0.60)],
      });
    }
  }
  tyreTracks(w, { a: [-24, -30], b: [26, -25], gauge: 1.8, y: 0.0082, mat: 'asphalt', tint: [1.05, 1.00, 0.88] });
  tyreTracks(w, { a: [-22, 30], b: [28, 25], gauge: 1.8, y: 0.0082, mat: 'asphalt', tint: [1.08, 1.02, 0.90] });
  tyreTracks(w, { a: [-24, -19.5], b: [28, -19.0], gauge: 1.72, y: 0.0082, mat: 'asphalt', tint: [1.02, 0.98, 0.86] });
}

/* ---- LEFT LANE: the alley ------------------------------------------ */

function buildAlley(w) {
  const rng = w.rng;
  const AX = ALLEY.x0;      // west wall of the alley (block A east face)
  const BX = ALLEY.x1;      // east wall of the alley (block B west face)

  /* --- Block A: the western boundary mass, three chunks for silhouette --- */
  const chunks = [
    { z0: -34, z1: -14, storeys: 3, h: 3.5, faces: { e: true, n: true } },
    { z0: -14, z1: 6, storeys: 2, h: 3.9, faces: { e: true } },
    { z0: 6, z1: 34, storeys: 3, h: 3.3, faces: { e: true, s: true } },
  ];
  for (const c of chunks) {
    blockBuilding(w, {
      x0: -36, z0: c.z0, x1: AX, z1: c.z1,
      storeys: c.storeys, storeyH: c.h,
      faces: c.faces,
      balconies: 0, clutter: true,
      groundOpenings: (len) => {
        const o = windowRow(len, { w: 1.0, h: 1.9, y: 0.9, gap: 4.0 });
        if (o.length) { o[Math.floor(o.length / 2)] = { x: o[Math.floor(o.length / 2)].x, y: 0, w: 1.2, h: 2.25, arch: true, sill: false, lintel: false }; }
        return o;
      },
    });
  }

  /* --- Block B: low block between alley and square, walkable roof ------ */
  // solid cores either side of the through-passage
  for (const [z0, z1] of [[BLOCKB.z0, PASS.z0], [PASS.z1, BLOCKB.z1]]) {
    const core = chamferBox(BLOCKB.x1 - BLOCKB.x0 - 0.5, BLOCKB.wallH, z1 - z0 - 0.1, 0.06);
    w.batch.add(core, 'concrete_wall', {
      matrix: xform([(BLOCKB.x0 + BLOCKB.x1) / 2, BLOCKB.wallH / 2, (z0 + z1) / 2], 0),
      surface: 'concrete', uvScale: 2.4, tint: [0.7, 0.68, 0.64],
    });
    core.dispose();
    w.blockerBox(BLOCKB.x0, 0, z0, BLOCKB.x1, BLOCKB.wallH, z1, 'concrete');
  }
  // slab over the passage
  w.blockerBox(BLOCKB.x0, 3.0, PASS.z0, BLOCKB.x1, BLOCKB.wallH, PASS.z1, 'concrete');
  slab(w, { x0: BLOCKB.x0, z0: PASS.z0, x1: BLOCKB.x1, z1: PASS.z1, y: 3.3, thickness: 0.3, collide: false });

  // roof terrace + parapet
  slab(w, { x0: BLOCKB.x0 - 0.25, z0: BLOCKB.z0 - 0.25, x1: BLOCKB.x1 + 0.25, z1: BLOCKB.z1 + 0.25, y: BLOCKB.roof, thickness: 0.34, mat: 'concrete_floor', uvScale: 2.0 });
  /*
   * Parapet runs, stepped along their own length.
   *
   * `step: false` is reserved for the east edge, which is a head-glitch line the
   * right lane is balanced around — its height is a gameplay number, not an art
   * one. Everywhere else the run breaks into 2.5–5 m lengths at different
   * heights with the coping stepping with them, plus the odd raised pier, so no
   * single straight edge survives for more than a few metres.
   */
  const parapetRun = (isX, fixed, h, a0, a1, step = true) => {
    const L = a1 - a0;
    const segs = step ? Math.max(2, Math.min(5, Math.round(L / 3.6))) : 1;
    let cur = a0, prev = null;
    for (let s = 0; s < segs; s++) {
      const want = s === segs - 1 ? (a1 - cur) : (L / segs) * rng.range(0.62, 1.42);
      const sl = Math.min(want, a1 - cur);
      if (sl < 0.35) break;
      const mid = cur + sl / 2, start = cur;
      cur += sl;
      let ph = step ? h * rng.range(0.62, 1.42) : h;
      if (step && prev !== null && Math.abs(ph - prev) < 0.18) ph = prev + rng.sign() * rng.range(0.2, 0.5);
      ph = Math.max(0.32, Math.min(h * 2.0, ph));
      const cx = isX ? mid : fixed, cz = isX ? fixed : mid;
      const g = isX ? chamferBox(sl, ph, 0.3, 0.045) : chamferBox(0.3, ph, sl, 0.045);
      w.batch.add(g, 'plaster_wall', { matrix: xform([cx, BLOCKB.roof + ph / 2, cz], 0), surface: 'concrete', uvScale: 1.4 });
      g.dispose();
      if (!step || rng() > 0.15) {
        const cap = isX ? chamferBox(sl, 0.10, 0.46, 0.032) : chamferBox(0.46, 0.10, sl, 0.032);
        w.batch.add(cap, 'concrete_wall', { matrix: xform([cx, BLOCKB.roof + ph + 0.045, cz], 0), surface: 'concrete', uvScale: 0.7 });
        cap.dispose();
      }
      if (step && rng() < 0.5 && sl > 1.0) {
        const pierH = ph + rng.range(0.24, 0.72), pw = rng.range(0.32, 0.5);
        const pu = start + (rng() < 0.5 ? 0.18 : sl - 0.18);
        const qx = isX ? pu : fixed, qz = isX ? fixed : pu;
        const p = isX ? chamferBox(pw, pierH, 0.4, 0.035) : chamferBox(0.4, pierH, pw, 0.035);
        w.batch.add(p, 'plaster_wall', { matrix: xform([qx, BLOCKB.roof + pierH / 2, qz], 0), surface: 'concrete', uvScale: 1.2 });
        p.dispose();
        const pc = isX ? chamferBox(pw + 0.14, 0.09, 0.52, 0.026) : chamferBox(0.52, 0.09, pw + 0.14, 0.026);
        w.batch.add(pc, 'concrete_wall', { matrix: xform([qx, BLOCKB.roof + pierH + 0.045, qz], 0), surface: 'concrete', uvScale: 0.6 });
        pc.dispose();
      }
      prev = ph;
    }
    // one collider at the authored height: the steps are silhouette, the
    // collision line is the gameplay contract and must not wobble with the RNG
    if (isX) w.blockerBox(a0, BLOCKB.roof, fixed - 0.18, a1, BLOCKB.roof + h, fixed + 0.18, 'concrete', false);
    else w.blockerBox(fixed - 0.18, BLOCKB.roof, a0, fixed + 0.18, BLOCKB.roof + h, a1, 'concrete', false);
  };
  const parapetX = (z, h, x0, x1, step = true) => parapetRun(true, z, h, x0, x1, step);
  const parapetZ = (x, h, z0, z1, step = true) => parapetRun(false, x, h, z0, z1, step);
  parapetX(BLOCKB.z0 - 0.1, 1.0, BLOCKB.x0 - 0.25, BLOCKB.x1 + 0.25);
  // south edge, split to leave the external stair a landing
  parapetX(BLOCKB.z1 + 0.1, 1.0, BLOCKB.x0 - 0.25, -18.0);
  parapetX(BLOCKB.z1 + 0.1, 1.0, -15.6, BLOCKB.x1 + 0.25);
  // east parapet (facing the square) — head-glitch height, blown open mid-run
  parapetZ(BLOCKB.x1 + 0.1, 1.05, BLOCKB.z0 - 0.1, -1.6, false);
  parapetZ(BLOCKB.x1 + 0.1, 1.05, 0.1, BLOCKB.z1 + 0.1, false);
  {
    /*
     * Jagged stubs of the blown parapet. This is the foreground of
     * `level_rooftop`, so it has to survive being 3 m from the camera: the
     * stubs are pitched and rolled out of alignment (three clean upright boxes
     * read as a fence, not as damage), each is a different height, and the
     * exposed brick core is dusted down rather than left as bright red.
     */
    const stubs = [
      [-1.52, 0.66, 0.40], [-1.16, 0.30, 0.34], [-0.86, 0.51, 0.30],
      [-0.42, 0.19, 0.36], [-0.06, 0.48, 0.44], [0.16, 0.27, 0.26],
    ];
    for (const [z, h, len] of stubs) {
      // A 5 cm chamfer on a broken wall reads as a soap bar. Masonry snaps
      // sharp, so the chamfer here is the smallest the foundry allows and the
      // silhouette comes from stacked, offset, differently-rolled fragments.
      const stub = chamferBox(0.3, h, len, 0.026);
      w.batch.add(stub, 'plaster_wall', {
        matrix: xform([BLOCKB.x1 + 0.1 + rng.range(-0.03, 0.03), BLOCKB.roof + h / 2, z],
          [rng.range(-0.10, 0.10), rng.range(-0.12, 0.12), rng.range(-0.13, 0.13)]),
        surface: 'concrete', uvScale: 1.2, tint: [rng.range(0.72, 0.94), rng.range(0.71, 0.93), rng.range(0.70, 0.92)],
      });
      stub.dispose();
      // chipped shoulders: two smaller blocks knocked off the top of the stub
      for (let c = 0; c < 2; c++) {
        const cw = rng.range(0.10, 0.20), ch = rng.range(0.06, 0.15), cd = len * rng.range(0.3, 0.6);
        const chip = chamferBox(cw, ch, cd, 0.014);
        w.batch.add(chip, 'plaster_wall', {
          matrix: xform([BLOCKB.x1 + 0.1 + rng.range(-0.11, 0.11), BLOCKB.roof + h - rng.range(0.0, 0.10), z + rng.range(-len * 0.35, len * 0.35)],
            [rng.range(-0.4, 0.4), rng.range(-0.5, 0.5), rng.range(-0.4, 0.4)]),
          surface: 'concrete', uvScale: 0.9, tint: [rng.range(0.74, 0.96), rng.range(0.73, 0.95), rng.range(0.72, 0.93)],
        });
        chip.dispose();
      }
      // snapped brick core, exposed where the render spalled off
      const core = chamferBox(0.245, h * rng.range(0.5, 0.78), len * 0.8, 0.022);
      const v = rng.range(0.56, 0.78);
      w.batch.add(core, 'concrete_wall', {
        matrix: xform([BLOCKB.x1 + 0.1, BLOCKB.roof + h * 0.3, z], [0, 0, rng.range(-0.09, 0.09)]),
        surface: 'concrete', uvScale: 0.5, tint: [v * 1.05, v * 0.98, v * 0.9],
      });
      core.dispose();
    }
    // the coping that came off, tumbled on the deck behind the breach
    for (const [dx, dz, rot] of [[-0.75, -1.05, 0.42], [-1.35, 0.15, -0.9], [-0.55, 0.62, 1.35]]) {
      const cap = chamferBox(0.46, 0.10, rng.range(0.5, 0.9), 0.032);
      w.batch.add(cap, 'concrete_wall', {
        matrix: xform([BLOCKB.x1 + 0.1 + dx, BLOCKB.roof + 0.05, -0.75 + dz],
          [rng.range(-0.06, 0.06), rot, rng.range(-0.05, 0.05)]),
        surface: 'concrete', uvScale: 0.7, tint: [rng.range(0.78, 0.98), rng.range(0.77, 0.96), rng.range(0.75, 0.94)],
      });
      cap.dispose();
    }
    // out of the DECK, not 0.30 m above it: these are the rust poles that read as
    // stabbing through the plaza with no base in `level_rooftop`.
    rebar(w, { x: BLOCKB.x1 + 0.05, y: BLOCKB.roof + 0.10, z: -0.75, count: 5, spread: 0.5, len: 0.75 });
    rubblePile(w, { x: BLOCKB.x1 - 1.0, z: -0.75, y: BLOCKB.roof, radius: 1.0, count: 8, bricks: 9, bar: false });
  }
  // west parapet, low, split for the plank bridge
  parapetZ(BLOCKB.x0 - 0.1, 0.72, BLOCKB.z0 - 0.1, 1.0);
  parapetZ(BLOCKB.x0 - 0.1, 0.72, 2.8, BLOCKB.z1 + 0.1);

  /* --- Block B facades ------------------------------------------------- */
  // alley (west) face — arched passage + doors
  wall(w, {
    a: [BLOCKB.x0, BLOCKB.z0], b: [BLOCKB.x0, BLOCKB.z1], base: 0, height: BLOCKB.wallH, thickness: 0.42,
    mat: 'plaster_wall', uvScale: 1.7, damage: 3,
    cornice: { y: BLOCKB.wallH, proj: 0.2, h: 0.24, side: 'one' },
    reveal: true,
    openings: [
      { x: (PASS.z0 + PASS.z1) / 2 - (BLOCKB.z0 + BLOCKB.z1) / 2, y: 0, w: 3.4, h: 3.0, arch: true, sill: false, lintel: false },
      { x: -15.5, y: 0, w: 1.15, h: 2.25, arch: true, sill: false, lintel: false },
      { x: -8.0, y: 1.25, w: 0.85, h: 1.15, grille: true, glass: 'dirty' },
      { x: 4.5, y: 0, w: 1.2, h: 2.3, sill: false },
      { x: 9.5, y: 1.3, w: 0.9, h: 1.2, grille: true, glass: 'dirty' },
      { x: 15.0, y: 1.3, w: 0.9, h: 1.2, grille: true, glass: 'dirty' },
    ],
    collide: false,
  });
  // square (east) face — shopfronts under awnings
  wall(w, {
    a: [BLOCKB.x1, BLOCKB.z1], b: [BLOCKB.x1, BLOCKB.z0], base: 0, height: BLOCKB.wallH, thickness: 0.42,
    mat: 'stucco', uvScale: 1.7, damage: 2,
    cornice: { y: BLOCKB.wallH, proj: 0.22, h: 0.26, side: 'one' },
    reveal: true,
    openings: [
      { x: -(PASS.z0 + PASS.z1) / 2 + (BLOCKB.z0 + BLOCKB.z1) / 2, y: 0, w: 3.4, h: 3.0, arch: true, sill: false, lintel: false },
      { x: -13.0, y: 0.2, w: 2.6, h: 2.3, glass: 'dirty', shutter: true, sill: false },
      { x: -7.0, y: 0, w: 1.3, h: 2.35, sill: false },
      { x: 4.0, y: 0.2, w: 2.4, h: 2.3, glass: 'dirty', shutter: true, sill: false },
      { x: 11.5, y: 0.2, w: 2.4, h: 2.3, glass: 'dirty', shutter: true, sill: false },
      { x: 17.5, y: 0, w: 1.3, h: 2.35, sill: false },
    ],
    collide: false,
  });
  // north + south end walls
  wall(w, {
    a: [BLOCKB.x1, BLOCKB.z0], b: [BLOCKB.x0, BLOCKB.z0], base: 0, height: BLOCKB.wallH, thickness: 0.42,
    mat: 'plaster_wall', damage: 2, collide: false,
    openings: [{ x: 0, y: 1.2, w: 1.0, h: 1.3, grille: true, glass: 'dirty' }],
    cornice: { y: BLOCKB.wallH, proj: 0.2, h: 0.24, side: 'one' },
  });
  wall(w, {
    a: [BLOCKB.x0, BLOCKB.z1], b: [BLOCKB.x1, BLOCKB.z1], base: 0, height: BLOCKB.wallH, thickness: 0.42,
    mat: 'plaster_wall', damage: 2, collide: false,
    openings: [{ x: -1.4, y: 0, w: 1.25, h: 2.3, arch: true, sill: false, lintel: false }],
    cornice: { y: BLOCKB.wallH, proj: 0.2, h: 0.24, side: 'one' },
  });

  // passage interior: side walls, ceiling, a light
  for (const z of [PASS.z0, PASS.z1]) {
    const g = chamferBox(BLOCKB.x1 - BLOCKB.x0, 3.0, 0.18, 0.04);
    w.batch.add(g, 'plaster_wall', { matrix: xform([(BLOCKB.x0 + BLOCKB.x1) / 2, 1.5, z], 0), surface: 'concrete', uvScale: 1.4, tint: [0.9, 0.88, 0.85] });
    g.dispose();
  }
  groundPatch(w, { x0: BLOCKB.x0, z0: PASS.z0, x1: BLOCKB.x1, z1: PASS.z1, y: 0.012, mat: 'concrete_floor', surface: 'concrete', res: 0.6, uvScale: 1.2, tint: [0.85, 0.84, 0.82] });
  hangingBulb(w, { x: (BLOCKB.x0 + BLOCKB.x1) / 2, y: 2.98, z: (PASS.z0 + PASS.z1) / 2, drop: 0.5, intensity: 3.0, radius: 6.5 });
  w.light([(BLOCKB.x0 + BLOCKB.x1) / 2, 1.6, (PASS.z0 + PASS.z1) / 2], 0xb9c6d8, 0.7, 6, 'room', { role: 'passage_fill' });

  /* --- free-standing arches over the alley ----------------------------- */
  // (rotY = 0: the arch spans across X, its thickness runs along the alley)
  for (const z of [-22, -3.0, 12.5, 25]) {
    archway(w, {
      x: (AX + BX) / 2, z, rotY: 0, span: BX - AX - 0.6, pierHeight: 2.55,
      thickness: 0.95, jamb: 0.62, mat: 'plaster_wall',
    });
  }

  /* --- stairs up to block A's balcony walkway --------------------------- */
  const stairX = AX + 1.05;
  stairs(w, { x: stairX, z: -16.4, y: 0, rotY: 0, steps: 20, rise: 0.215, run: 0.285, width: 1.65, stringer: 1, rail: 'right', mat: 'concrete_floor' });

  // Block A balcony walkway (north end left open — that is where the stair lands)
  balcony(w, {
    x0: AX - 0.15, z0: -10.6, x1: AX + 2.15, z1: 12.0, y: 4.3, thickness: 0.28,
    open: [
      [[AX + 2.15, 4.3, -10.6], [AX + 2.15, 4.3, 0.9]],
      [[AX + 2.15, 4.3, 2.9], [AX + 2.15, 4.3, 12.0]],
      [[AX - 0.15, 4.3, 12.0], [AX + 2.15, 4.3, 12.0]],
    ],
  });
  /*
   * Shade canopies over the balcony walkway.
   *
   * These were the "flat brown panels hovering in mid-air" in `level_square` and
   * across the top of `level_alley`. They were authored at x = AX + 1.0, which is
   * 1.34 m clear of block A's facade face (x = -25.66) and 2.2 m above the
   * balcony deck, with `poles` off — so nothing whatsoever touched them at either
   * end. They now start ON the facade and stand on two poles planted on the
   * balcony deck at y = 4.3, and `ctx.level.verifyOverheads()` proves it.
   */
  for (let i = 0; i < 3; i++) {
    awning(w, {
      x: AX + 0.42, z: -6 + i * 5.4, y: 6.5, rotY: Math.PI / 2,
      width: 4.6, depth: 1.65, drop: 0.5,
      poles: true, poleBase: 4.3, tint: AWNING[i % AWNING.length],
    });
  }

  // plank bridge from the walkway to block B's roof
  {
    const bz = 1.9;
    for (let i = 0; i < 5; i++) {
      const g = chamferBox(BX - AX - 1.9, 0.075, 0.30, 0.026);
      w.batch.add(g, 'wood_plank', { matrix: xform([(AX + BX) / 2 + 0.95, 4.31, bz - 0.6 + i * 0.32], 0), surface: 'wood', uvScale: 0.5 });
      g.dispose();
    }
    for (const s of [-1, 1]) {
      const g = chamferBox(BX - AX - 1.7, 0.16, 0.12, 0.03);
      w.batch.add(g, 'wood_plank', { matrix: xform([(AX + BX) / 2 + 0.95, 4.2, bz + s * 0.72], 0), surface: 'wood', uvScale: 0.4 });
      g.dispose();
    }
    w.blockerBox(AX + 2.0, 4.15, bz - 0.85, BX + 0.2, 4.32, bz + 0.85, 'wood', false);
    railing(w, { a: [AX + 2.1, 4.32, bz - 0.8], b: [BX + 0.1, 4.32, bz - 0.8], height: 0.95, infill: 8 });
    railing(w, { a: [AX + 2.1, 4.32, bz + 0.8], b: [BX + 0.1, 4.32, bz + 0.8], height: 0.95, infill: 8 });
  }

  // south descent from block B's roof into the cross street (lands in the parapet gap)
  stairs(w, { x: -16.8, z: 21.95, y: 0, rotY: Math.PI, steps: 20, rise: 0.215, run: 0.285, width: 1.7, rail: 'both', mat: 'concrete_floor' });
  ladder(w, { x: BLOCKB.x0 + 1.2, z: BLOCKB.z0 + 0.35, y0: 0.2, y1: BLOCKB.roof + 0.4, rotY: Math.PI });

  /* --- alley dressing --------------------------------------------------- */
  for (let i = 0; i < 7; i++) {
    const z = -30 + i * 9 + rng.range(-1.5, 1.5);
    laundryLine(w, [AX + 0.4, rng.range(5.4, 7.4), z], [BX - 0.4, rng.range(5.2, 7.2), z + rng.range(-1, 1)], { items: rng.int(3, 5), sag: 0.55 });
  }
  for (let i = 0; i < 6; i++) {
    const z = -28 + i * 10.5;
    wireBundle(w, [AX - 0.2, 8.4, z], [BX + 0.2, 6.1, z + 2.5], { count: 3, sag: 0.9, spread: 0.16 });
  }
  for (const z of [-24, -12, 2, 16, 28]) {
    wallLamp(w, { x: AX + 0.4, z, y: 3.2, rotY: Math.PI / 2, intensity: 3.2, radius: 7.5 });
  }
  for (const z of [-19, -1, 15]) {
    drainpipe(w, { x: AX + 0.46, z, y0: 0.05, y1: 9.2, rotY: Math.PI / 2 });
  }
  conduit(w, [[BX - 0.36, 3.1, -25], [BX - 0.36, 3.1, -8], [BX - 0.36, 2.4, -7.4], [BX - 0.36, 2.4, 14]], { boxes: 2 });
  for (const z of [-20.5, -9, 7.5, 20]) {
    acUnit(w, { x: BX - 0.5, y: 2.6, z, rotY: -Math.PI / 2, scale: 0.95 });
  }
  for (const z of [-16, 6]) {
    const c = RUG[rng.int(0, RUG.length - 1)];
    hangingFabric(w, { x: BX - 0.42, y: 3.0, z, rotY: -Math.PI / 2, width: 1.5, height: 2.0, tint: c });
  }

  // rubble, wear and weeds at the wall bases (the level_detail shot lives here)
  rubblePile(w, { x: BX - 1.15, z: -13.2, radius: 1.5, count: 9, bricks: 7 });
  rubblePile(w, { x: AX + 1.3, z: 8.5, radius: 1.2, count: 6, bricks: 5 });
  rubblePile(w, { x: BX - 1.0, z: 22.5, radius: 1.3, count: 6, bricks: 4 });
  // the sunlit west wall base — dressed for the close-up
  rubblePile(w, { x: AX + 1.25, z: -14.4, radius: 1.5, count: 10, bricks: 9 });
  w.props.place('cinder_block', xform([AX + 0.95, 0, -12.9], 0.42, 1.05));
  w.props.place('cinder_block', xform([AX + 1.35, 0.2, -13.0], 1.1, 1.0));
  w.props.place('bucket', xform([AX + 1.9, 0, -12.2], 0.9), variantTint('bucket', rng));
  for (const z of [-15.6, -13.9, -12.4]) {
    const plank = chamferBox(1.5, 0.055, 0.22, 0.022);
    w.batch.add(plank, 'wood_plank', {
      matrix: xform([AX + 0.62, 0.75 + rng.range(-0.06, 0.06), z], [0, rng.range(-0.1, 0.1), 1.18]),
      surface: 'wood', uvScale: 0.4,
    });
    plank.dispose();
  }
  weeds(w, { x0: ALLEY.x0 + 0.15, z0: -32, x1: ALLEY.x0 + 1.4, z1: 32, count: 110 });
  weeds(w, { x0: ALLEY.x1 - 1.4, z0: -32, x1: ALLEY.x1 - 0.15, z1: 32, count: 110 });
  for (const [x, z] of [[AX + 0.9, -27], [BX - 0.9, -18], [AX + 0.8, 4], [BX - 1.0, 27]]) {
    shrub(w, { x, y: 0.01, z, scale: w.rng.range(0.8, 1.3) });
  }
  scatter(w, 'crate_wood', { x0: ALLEY.x0 + 0.7, z0: -30, x1: ALLEY.x1 - 0.7, z1: 30, count: 9 });
  scatter(w, 'barrel', { x0: ALLEY.x0 + 0.8, z0: -30, x1: ALLEY.x1 - 0.8, z1: 30, count: 6 });
  scatter(w, 'cardboard', { x0: ALLEY.x0 + 0.6, z0: -30, x1: ALLEY.x1 - 0.6, z1: 30, count: 7 });
  scatter(w, 'pallet', { x0: ALLEY.x0 + 0.8, z0: -30, x1: ALLEY.x1 - 0.8, z1: 30, count: 5 });
  scatter(w, 'tyre', { x0: ALLEY.x0 + 0.8, z0: -30, x1: ALLEY.x1 - 0.8, z1: 30, count: 6 });
  scatter(w, 'bucket', { x0: ALLEY.x0 + 0.6, z0: -30, x1: ALLEY.x1 - 0.6, z1: 30, count: 5 });

  // roof-terrace dressing: patched bitumen, ponding stains, then the clutter
  for (let i = 0; i < 16; i++) {
    driftPatch(w, {
      x: rng.range(BLOCKB.x0 + 0.6, BLOCKB.x1 - 0.6), z: rng.range(BLOCKB.z0 + 1, BLOCKB.z1 - 1),
      rx: rng.range(0.8, 2.6), rz: rng.range(0.8, 3.0), rot: rng.range(0, Math.PI),
      y: BLOCKB.roof + 0.012, mat: 'asphalt', surface: 'concrete',
      tint: (() => { const k = rng.range(0.72, 1.05); return [1.18 * k, 1.06 * k, 0.86 * k]; })(),
    });
  }
  for (let i = 0; i < 8; i++) {
    driftPatch(w, {
      x: rng.range(BLOCKB.x0 + 0.8, BLOCKB.x1 - 0.8), z: rng.range(BLOCKB.z0 + 1, BLOCKB.z1 - 1),
      rx: rng.range(0.6, 1.7), rz: rng.range(0.6, 1.6), rot: rng.range(0, Math.PI),
      y: BLOCKB.roof + 0.02, mat: 'gravel', surface: 'dirt', tint: [0.8, 0.78, 0.72],
    });
  }
  // tar seams over the joins, then ponding stains where the fall is wrong.
  // Without these a flat roof reads as one printed material at any distance.
  crackScatter(w, { x0: BLOCKB.x0 + 0.6, z0: BLOCKB.z0 + 1, x1: BLOCKB.x1 - 0.6, z1: BLOCKB.z1 - 1, count: 16, y: BLOCKB.roof + 0.016, mat: 'asphalt' });
  for (let i = 0; i < 5; i++) {
    driftPatch(w, {
      x: rng.range(BLOCKB.x0 + 1, BLOCKB.x1 - 1), z: rng.range(BLOCKB.z0 + 2, BLOCKB.z1 - 2),
      rx: rng.range(0.9, 2.2), rz: rng.range(0.9, 2.4), rot: rng.range(0, Math.PI),
      y: BLOCKB.roof + 0.024, mat: 'concrete_floor', surface: 'concrete',
      tint: [0.60, 0.62, 0.66], seg: 24,
    });
  }
  // silver-painted patch repairs, the tell of a roof that has been mended twice
  for (let i = 0; i < 4; i++) {
    const pw = rng.range(1.0, 2.4), pd = rng.range(0.8, 1.8);
    const g = quadXZ(pw, pd);
    w.batch.add(g, 'concrete_floor', {
      matrix: xform([rng.range(BLOCKB.x0 + 1.2, BLOCKB.x1 - 1.2), BLOCKB.roof + 0.03, rng.range(BLOCKB.z0 + 2, BLOCKB.z1 - 2)], rng.range(-0.25, 0.25)),
      surface: 'concrete', uvScale: 1.1, cast: false, tint: [1.22, 1.20, 1.16],
    });
    g.dispose();
  }
  roofClutter(w, { x0: BLOCKB.x0 + 1, z0: BLOCKB.z0 + 2, x1: BLOCKB.x1 - 1, z1: -8, y: BLOCKB.roof, density: 0.9 });
  roofClutter(w, { x0: BLOCKB.x0 + 1, z0: 4, x1: BLOCKB.x1 - 1, z1: BLOCKB.z1 - 2, y: BLOCKB.roof, density: 0.9 });
  scatter(w, 'crate_wood_lg', { x0: BLOCKB.x0 + 1.2, z0: -22, x1: BLOCKB.x1 - 1.2, z1: 12, count: 6, y: BLOCKB.roof });
  sandbagWall(w, { a: [BLOCKB.x1 - 0.9, -4.6], b: [BLOCKB.x1 - 0.9, 1.2], rows: 3, y: BLOCKB.roof });
  sandbagWall(w, { a: [BLOCKB.x1 - 2.6, 6.4], b: [BLOCKB.x1 - 0.9, 6.4], rows: 3, y: BLOCKB.roof });
  for (const [x, z] of [[-17.4, -14], [-15.5, 9.5]]) {
    waterTank(w, { x, y: BLOCKB.roof, z, r: 0.6, h: 1.0, stand: 0.95 });
  }
  laundryLine(w, [BLOCKB.x0 + 1.0, BLOCKB.roof + 1.9, -10], [BLOCKB.x1 - 1.0, BLOCKB.roof + 1.75, -7.5], { items: 4, sag: 0.4 });

  /*
   * Roofline pass on block B's terrace.
   *
   * This is the foreground and the mid-ground of `level_rooftop`, and until now
   * everything on it was below the 1.05 m parapet, so the block's top edge was a
   * flat coping against the sky in every frame that saw it. Solid, because this
   * is one of the two roofs the player actually stands on.
   */
  rooflineClutter(w, {
    x0: BLOCKB.x0 - 0.1, z0: BLOCKB.z0, x1: BLOCKB.x1 + 0.1, z1: -3.0,
    y: BLOCKB.roof, parapetH: 1.05, density: 1.15, solid: true, edges: 'nw', maxItems: 7,
  });
  rooflineClutter(w, {
    x0: BLOCKB.x0 - 0.1, z0: 4.0, x1: BLOCKB.x1 + 0.1, z1: BLOCKB.z1,
    y: BLOCKB.roof, parapetH: 1.05, density: 1.0, solid: true, edges: 'nsw', maxItems: 7,
  });
  // the square-facing edge stays deliberately thin — it is the firing line —
  // but it still gets two verticals so it is not a bare coping run
  cablePole(w, { x: BLOCKB.x1 - 0.55, y: BLOCKB.roof, z: -18.5, h: 3.4, rotY: 0, arms: 2 });
  cablePole(w, { x: BLOCKB.x1 - 0.55, y: BLOCKB.roof, z: 12.0, h: 3.0, rotY: 0, arms: 1 });
  laundryPost(w, { x: BLOCKB.x1 - 0.9, y: BLOCKB.roof, z: 8.6, rotY: 0, span: 3.0, h: 2.1, items: 4 });
}

/* ---- CENTRE LANE: the market square -------------------------------- */

function buildSquare(w) {
  const rng = w.rng;

  /* --- dry fountain --------------------------------------------------- */
  const FX = 0.5, FZ = -1.0, FR = 3.3;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const px = FX + Math.cos(a) * (FR - 0.22), pz = FZ + Math.sin(a) * (FR - 0.22);
    const seg = chamferBox(2 * FR * Math.tan(Math.PI / 8) + 0.1, 0.92, 0.44, 0.052);
    w.batch.add(seg, 'concrete_wall', { matrix: xform([px, 0.46, pz], -a + Math.PI / 2), surface: 'concrete', uvScale: 1.0, tint: [1.02, 1.0, 0.96] });
    seg.dispose();
    const cap = chamferBox(2 * FR * Math.tan(Math.PI / 8) + 0.22, 0.12, 0.60, 0.038);
    w.batch.add(cap, 'concrete_wall', { matrix: xform([px, 0.965, pz], -a + Math.PI / 2), surface: 'concrete', uvScale: 0.8 });
    cap.dispose();
    w.blocker(px, 0.5, pz, 2 * FR * Math.tan(Math.PI / 8) + 0.1, 1.02, 0.6, -a + Math.PI / 2, 'concrete');
  }
  groundPatch(w, { x0: FX - FR, z0: FZ - FR, x1: FX + FR, z1: FZ + FR, y: 0.03, mat: 'concrete_floor', surface: 'concrete', res: 0.6, uvScale: 1.2, tint: [0.78, 0.76, 0.7] });
  // central plinth + broken spout column
  const plinth = chamferCyl(1.05, 0.55, 12, 0.05);
  w.batch.add(plinth, 'concrete_wall', { matrix: xform([FX, 0.3, FZ], 0), surface: 'concrete', uvScale: 0.9 });
  plinth.dispose();
  const col = taperCyl(0.42, 0.30, 1.5, 10);
  w.batch.add(col, 'concrete_wall', { matrix: xform([FX, 1.32, FZ], 0), surface: 'concrete', uvScale: 0.8 });
  col.dispose();
  const bowl = taperCyl(0.22, 0.72, 0.36, 12);
  w.batch.add(bowl, 'concrete_wall', { matrix: xform([FX, 2.22, FZ], 0), surface: 'concrete', uvScale: 0.7 });
  bowl.dispose();
  rebar(w, { x: FX, y: 2.36, z: FZ, count: 3, spread: 0.18, len: 0.5, collar: false });
  w.blocker(FX, 1.3, FZ, 1.0, 2.6, 1.0, 0, 'concrete');
  rubblePile(w, { x: FX + 2.1, z: FZ + 2.6, radius: 1.1, count: 5, bricks: 4, bar: false });
  weeds(w, { x0: FX - FR, z0: FZ - FR, x1: FX + FR, z1: FZ + FR, count: 22 });

  /* --- market stalls -------------------------------------------------- */
  const stallSpots = [
    [-9.4, -9.5, 0.02], [-9.4, -4.0, -0.05], [-9.6, 3.4, 0.04], [-9.4, 8.8, -0.03],
    [8.6, -11.0, Math.PI + 0.05], [8.8, -5.4, Math.PI - 0.04], [8.6, 2.2, Math.PI + 0.03], [8.8, 8.0, Math.PI - 0.02],
    [-2.5, 12.6, -Math.PI / 2 + 0.05], [3.0, -13.2, Math.PI / 2 - 0.04],
  ];
  for (const [x, z, r] of stallSpots) {
    w.props.place('stall', xform([x, 0, z], r, rng.range(0.97, 1.04)),
      [rng.range(0.92, 1.08), rng.range(0.9, 1.06), rng.range(0.86, 1.02)]);
    /*
     * Canopy over the stall frame.
     *
     * It used to spring from the stall's CENTRE and project backwards over
     * nothing, so its front edge cantilevered 1.15 m into open air. It now sits
     * on the stall's own back rail (prop-local z = -0.48, rail top y 2.36) and
     * runs forward over the counter onto two front poles that reach the paving.
     */
    const base = AWNING[rng.int(0, AWNING.length - 1)];
    const k = rng.range(0.85, 1.12);
    const tint = [base[0] * k, base[1] * k, base[2] * k];
    awning(w, {
      x: x - Math.sin(r) * 0.48, z: z - Math.cos(r) * 0.48, y: 2.40, rotY: r,
      width: 2.5, depth: 1.55, drop: 0.40, poles: true, tint, mat: 'canvas',
    });
    // goods on the counter
    for (let i = 0; i < 3; i++) {
      const u = -0.75 + i * 0.75;
      w.props.place('produce_crate', xform([x + Math.cos(r) * u + Math.sin(r) * 0.05, 0.99, z - Math.sin(r) * u + Math.cos(r) * 0.05], r + rng.range(-0.2, 0.2)), variantTint('produce_crate', rng));
    }
    for (let i = 0; i < 2; i++) {
      const u = rng.range(-1.0, 1.0);
      w.props.place('basket', xform([x + Math.cos(r) * u + Math.sin(r) * 0.9, 0, z - Math.sin(r) * u + Math.cos(r) * 0.9], rng.range(0, 6.28)), variantTint('basket', rng));
    }
    w.blocker(x, 0.55, z, 2.5, 1.1, 1.2, r, 'wood');
    w.light([x + Math.sin(r) * 0.2, 2.2, z + Math.cos(r) * 0.2], 0xffcf9a, 1.4, 4.0, 'point', { role: 'stall_lamp' });
  }
  /*
   * Souk canopy — the defect the reviewer found twice and I reported fixed once.
   *
   * The old version hung nine cloth panels off two 2 cm cables carried on THREE
   * posts across 15.6 m. A 2 cm cable is sub-pixel at 20 m, so what the frame
   * actually showed was a row of flat brown plates floating over the square with
   * 7.8 m of clear air under the middle of every span — exactly the note. The
   * structure is now legible from any distance:
   *   - 5 post pairs per run, so no span exceeds 3.9 m;
   *   - 7.5 cm steel top rails, not cable, with the cable kept as a secondary tie;
   *   - a cross batten under EACH HALF of every panel, so there is always
   *     something directly beneath the cloth;
   *   - the cloth sags onto the battens instead of floating flat above them.
   * The gaps between panels are still the point: they stripe the paving.
   */
  for (const [z, len] of [[-7.4, 15.6], [5.6, 15.6]]) {
    const x0 = -8.4, x1 = x0 + len;
    const RAIL = [{ z: z - 1.35, y: 3.32 }, { z: z + 1.35, y: 3.36 }];
    const POSTS = 5;
    for (const r of RAIL) {
      // top rail — real structure, thick enough to read at 20 m
      const rail = chamferBox(len + 0.9, 0.075, 0.075, 0.02);
      w.batch.add(rail, 'metal_rusted', {
        matrix: alignX([x0 - 0.45, r.y, r.z], [x1 + 0.45, r.y, r.z]), surface: 'metal', uvScale: 0.3,
      });
      rail.dispose();
      // secondary tie cable, slung just under the rail
      const cable = tube([[x0 - 0.4, r.y - 0.07, r.z], [(x0 + x1) / 2, r.y - 0.15, r.z], [x1 + 0.4, r.y - 0.07, r.z]], 0.026, 5);
      w.batch.add(cable, 'metal_rusted', { surface: 'metal', uvScale: 0.2, cast: false, tint: [0.4, 0.38, 0.36] });
      cable.dispose();
      for (let i = 0; i < POSTS; i++) {
        const px = x0 - 0.2 + ((len + 0.4) * i) / (POSTS - 1);
        const p = chamferCyl(0.062, r.y, 8, 0.016);
        w.batch.add(p, 'metal_rusted', { matrix: xform([px, r.y / 2, r.z], 0), surface: 'metal', uvScale: 0.3 });
        p.dispose();
        // splayed foot so the post meets the paving instead of ending in it
        const foot = chamferBox(0.26, 0.05, 0.26, 0.016);
        w.batch.add(foot, 'concrete_wall', { matrix: xform([px, 0.025, r.z], 0), surface: 'concrete', uvScale: 0.3, cast: false, tint: [0.8, 0.78, 0.75] });
        foot.dispose();
        // knee brace back to the rail — the other half of "this is held up"
        const bs = i === 0 ? 1 : (i === POSTS - 1 ? -1 : rng.sign());
        const A = [px, r.y - 0.62, r.z], B = [px + bs * 0.62, r.y - 0.04, r.z];
        const br = chamferBox(Math.hypot(B[0] - A[0], B[1] - A[1]), 0.045, 0.045, 0.012);
        w.batch.add(br, 'metal_rusted', { matrix: alignX(A, B), surface: 'metal', uvScale: 0.25 });
        br.dispose();
        w.blocker(px, r.y / 2, r.z, 0.14, r.y, 0.14, 0, 'metal', false);
      }
    }
    const panels = 9;
    const missing = new Set([rng.int(1, 3), rng.int(5, 7)]);
    for (let i = 0; i < panels; i++) {
      const px = x0 + (len * (i + 0.5)) / panels;
      const pw = (len / panels) * rng.range(0.68, 0.86);
      // cross battens: one under each half of the panel, spanning rail to rail
      for (const bx of [px - pw * 0.34, px + pw * 0.34]) {
        const bat = chamferBox(0.06, 0.06, 2.86, 0.016);
        w.batch.add(bat, 'wood_plank', { matrix: xform([bx, 3.245, z], 0), surface: 'wood', uvScale: 0.3, tint: [0.86, 0.8, 0.7] });
        bat.dispose();
      }
      if (missing.has(i)) {
        // torn away: only the stub still lashed to the near batten survives
        const stub = chamferBox((len / panels) * 0.5, 0.03, 0.55, 0.012);
        w.batch.add(stub, 'canvas', {
          matrix: xform([px - 0.2, 3.26 + rng.range(-0.04, 0.04), z - 1.1],
            [0.34, rng.range(-0.2, 0.2), 0], 1),
          surface: 'fabric', uvScale: 0.8, tint: [1.20, 1.06, 0.86],
        });
        stub.dispose();
        continue;
      }
      const droop = rng.range(0.09, 0.20);
      const lift = rng.range(-0.10, 0.12);
      // five strips per panel: the cloth now DIPS between the battens instead of
      // sitting as one flat plate above them
      const STRIPS = 5;
      for (let s = 0; s < STRIPS; s++) {
        const t = (s + 0.5) / STRIPS;
        const sag = Math.sin(t * Math.PI) * droop;
        const g = chamferBox(pw, 0.03, 0.62, 0.012);
        const warm = rng.range(0.9, 1.16);
        w.batch.add(g, 'canvas', {
          matrix: xform([px, 3.30 + lift - sag, z - 1.35 + t * 2.7],
            [(t - 0.5) * 0.34, rng.range(-0.05, 0.05), 0], 1),
          surface: 'fabric', uvScale: 0.8,
          tint: [1.30 * warm, 1.16 * warm, 0.94 * warm],
        });
        g.dispose();
      }
      w.overhead('souk_canopy', [
        [px - pw / 2, 3.28, z - 1.35], [px + pw / 2, 3.28, z - 1.35],
        [px + pw / 2, 3.28, z + 1.35], [px - pw / 2, 3.28, z + 1.35],
      ], { drop: 0.16, reach: 1.5 });
    }
  }

  /* --- hard cover: dead vehicles -------------------------------------- */
  sedan(w, { x: -5.6, z: 10.4, rotY: 0.42, tint: [0.86, 0.84, 0.78], flat: true, doorOpen: true });
  pickup(w, { x: 6.4, z: -10.8, rotY: -1.92, tint: [0.74, 0.7, 0.6] });
  sedan(w, { x: 11.6, z: 13.6, rotY: 2.55, tint: [0.6, 0.62, 0.66], flat: true });

  /* --- distributed cover ---------------------------------------------- */
  jerseyBarrier(w, { x: -2.2, z: -14.6, rotY: 0.06, len: 2.6 });
  jerseyBarrier(w, { x: 0.6, z: -14.55, rotY: -0.03, len: 2.6 });
  jerseyBarrier(w, { x: 4.0, z: 14.9, rotY: 0.05, len: 2.6 });
  jerseyBarrier(w, { x: -6.4, z: 15.0, rotY: -0.04, len: 2.6 });
  jerseyBarrier(w, { x: 13.4, z: -2.0, rotY: Math.PI / 2, len: 2.6 });

  sandbagWall(w, { a: [-11.8, -13.6], b: [-8.2, -13.6], rows: 4 });
  sandbagWall(w, { a: [12.2, 6.0], b: [12.2, 9.6], rows: 4 });
  sandbagWall(w, { a: [-1.2, 6.6], b: [2.4, 7.4], rows: 3 });

  // planter walls — head-glitch height, and they read as civic furniture
  for (const [x, z, r, len] of [[-12.0, 2.0, 0, 4.2], [13.6, -8.0, 0, 3.8], [-3.0, -16.0, Math.PI / 2, 3.0]]) {
    const g = chamferBox(0.5, 1.12, len, 0.05);
    w.batch.add(g, 'concrete_wall_painted', { matrix: xform([x, 0.56, z], r), surface: 'concrete', uvScale: 1.0 });
    g.dispose();
    const cap = chamferBox(0.66, 0.11, len + 0.16, 0.038);
    w.batch.add(cap, 'concrete_wall', { matrix: xform([x, 1.17, z], r), surface: 'concrete', uvScale: 0.7 });
    cap.dispose();
    w.blocker(x, 0.6, z, 0.66, 1.22, len + 0.16, r, 'concrete');
    const n = Math.max(1, Math.round(len / 1.4));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n - 0.5;
      shrub(w, { x: x + Math.sin(r) * 0, y: 1.2, z: z + t * len, scale: 1.05 });
    }
  }

  /* --- clutter, kept OUT of the movement lanes ------------------------- */
  // the two north-south running lanes and the fountain ring stay walkable
  // The last rect is the hand-dressed corner the `level_detail` close-up is
  // framed on: generic scatter dropping a plastic chair into that 2 m of
  // foreground is what a random scatter will eventually do, and it wrecks the
  // one shot in the set that has to hold up at 30 cm.
  const LANES = [
    [-6.4, -17, -2.6, 17], [2.4, -17, 6.2, 17], [-2.5, -17, 2.3, 17],
    [-4.6, -6.0, 5.6, 4.2], [-13.5, -17.0, -10.4, -8.5],
  ];
  const back = { x0: SQUARE.x0 + 1, z0: SQUARE.z0 + 1, x1: SQUARE.x1 - 1, z1: SQUARE.z1 - 1, avoid: LANES };
  /* --- the dressed corner the level_detail shot is framed on ------------ */
  {
    const wx = SQUARE.x0 + 0.24;   // outer face of block B's square-facing wall
    rubblePile(w, { x: wx + 1.0, z: -13.0, y: 0.16, radius: 1.35, count: 9, bricks: 10 });
    weeds(w, { x0: wx + 0.05, z0: -16.5, x1: wx + 1.5, z1: -9.0, count: 34, y: 0.15 });
    weeds(w, { x0: wx + 0.05, z0: -9.0, x1: wx + 1.2, z1: 14.0, count: 40, y: 0.15 });
    w.props.place('cinder_block', xform([wx + 0.55, 0.16, -11.6], 0.5, 1.05));
    w.props.place('cinder_block', xform([wx + 0.95, 0.36, -11.7], 1.25, 1.0));
    w.props.place('cinder_block', xform([wx + 0.72, 0.16, -12.15], 2.1, 1.0));
    w.props.place('bucket', xform([wx + 1.75, 0.16, -10.6], 0.9), variantTint('bucket', rng));
    w.props.place('tyre', xform([wx + 1.5, 0.16, -14.8], 0.7));
    for (const [z, ang] of [[-11.0, 1.16], [-11.35, 1.24]]) {
      const plank = chamferBox(1.7, 0.06, 0.24, 0.024);
      w.batch.add(plank, 'wood_plank', {
        matrix: xform([wx + 0.5, 0.95, z], [0, rng.range(-0.12, 0.12), ang]),
        surface: 'wood', uvScale: 0.4,
      });
      plank.dispose();
    }
    // blown plaster showing the brick, right where the camera lands
    // nrm: -1 — at rotY = -PI/2 the local +normal points into the wall, and the
    // square is on the other side
    damagePatch(w, { cx: wx - 0.02, cy: 0.95, cz: -13.7, rotY: -Math.PI / 2, nrm: -1, w: 1.7, h: 1.35, plasterMat: 'stucco' });
    damagePatch(w, { cx: wx - 0.02, cy: 0.55, cz: -10.2, rotY: -Math.PI / 2, nrm: -1, w: 1.1, h: 0.85, plasterMat: 'stucco' });
    rebar(w, { x: wx + 0.12, y: 0.32, z: -13.7, count: 3, spread: 0.35, len: 0.55 });
  }

  scatter(w, 'crate_wood', { ...back, count: 13 });
  scatter(w, 'crate_wood_lg', { ...back, count: 6 });
  scatter(w, 'crate_plastic', { ...back, count: 10 });
  scatter(w, 'barrel', { ...back, count: 7 });
  scatter(w, 'barrel_blue', { ...back, count: 5 });
  scatter(w, 'jerrycan', { ...back, count: 6 });
  scatter(w, 'chair', { ...back, count: 7 });
  scatter(w, 'tyre', { ...back, count: 6 });
  scatter(w, 'pallet', { ...back, count: 6 });
  scatter(w, 'pot_plant', { ...back, count: 8 });
  scatter(w, 'cinder_block', { ...back, count: 9 });
  scatter(w, 'gas_bottle', { ...back, count: 4 });
  scatter(w, 'basket', { ...back, count: 8 });
  // deliberate cover clusters — waist and head-glitch, on the lane shoulders
  const clusters = [[-7.6, -12.6], [-7.4, 1.4], [7.6, -3.2], [7.4, 11.6], [-11.6, 8.0], [12.6, 8.6]];
  for (const [cx, cz] of clusters) {
    const n = rng.int(3, 5);
    for (let i = 0; i < n; i++) {
      const a = rng.range(0, Math.PI * 2), r = rng.range(0.1, 0.85);
      const stacked = i > 0 && rng() < 0.45;
      const kk = rng() < 0.6 ? 'crate_wood_lg' : 'crate_wood';
      const cs = rng.range(0.92, 1.06);
      w.props.place(kk,
        xform([cx + Math.cos(a) * r, stacked ? 0.9 : 0, cz + Math.sin(a) * r],
          [rng.range(-0.05, 0.05), rng.range(0, 6.28), rng.range(-0.05, 0.05)],
          [cs * rng.range(0.9, 1.1), cs * rng.range(0.88, 1.12), cs * rng.range(0.9, 1.1)]),
        variantTint(kk, rng));
    }
    w.blocker(cx, 0.6, cz, 2.2, 1.2, 2.2, 0, 'wood');
    w.props.place('pallet', xform([cx + rng.range(-1.4, 1.4), 0, cz + rng.range(-1.4, 1.4)], rng.range(0, 6.28)));
  }
  w.props.place('table', xform([-10.4, 0, -3.2], 0.15));
  w.props.place('table', xform([9.6, 0, 6.4], Math.PI - 0.2));
  weeds(w, { x0: SQUARE.x0, z0: SQUARE.z0, x1: SQUARE.x1, z1: SQUARE.z1, count: 130 });

  /* --- trees and street furniture -------------------------------------- */
  for (const [x, z, s] of [[-11.8, -8.5, 1.05], [12.0, 4.6, 0.95], [-6.0, -16.2, 1.0]]) {
    w.props.place('olive_tree', xform([x, 0, z], rng.range(0, 6.28), s));
    w.blocker(x, 1.0, z, 0.5, 2.0, 0.5, 0, 'wood', false);
    weeds(w, { x0: x - 1.2, z0: z - 1.2, x1: x + 1.2, z1: z + 1.2, count: 8 });
  }
  for (const [x, z, s] of [[-12.4, 15.0, 1.0], [13.6, -15.0, 1.06], [-24.0, 30.5, 1.1], [22.0, -27.0, 1.0], [27.5, 24.0, 1.05]]) {
    w.props.place('palm_tree', xform([x, 0, z], rng.range(0, 6.28), s));
    w.blocker(x, 2.4, z, 0.55, 4.8, 0.55, 0, 'wood', false);
  }
  for (const [x, z] of [[-13.0, -14.0], [14.4, 12.0], [-13.0, 12.0], [14.4, -14.0]]) {
    w.props.place('street_lamp', xform([x, 0, z], Math.atan2(-x, -z)));
    w.blocker(x, 2.3, z, 0.28, 4.6, 0.28, 0, 'metal', false);
    w.light([x, 5.0, z + 1.0], 0xffd8a8, 2.0, 12, 'point', { role: 'streetlight' });
  }
  w.props.place('road_sign', xform([-13.2, 0, -18.6], 0.3), accentTint('blue', rng));
  w.props.place('road_sign', xform([15.6, 0, 18.4], Math.PI - 0.2), accentTint('yellow', rng));

  /*
   * Colour anchors.
   *
   * `level_overview` was marked "the entire frame is one sepia hue — automatic
   * fail". Spread jitter alone does not cure that: what `refs/mw3_04.jpg` does
   * is put a small number of STRONG, LOCAL accents into an otherwise warm frame
   * — a run of signal-orange barriers, one band of blue-white glazing — so the
   * eye reads colour without the palette widening. These are those accents, sited
   * on the centre lane where every hero camera sees them, and deliberately few.
   */
  const anchors = [
    ['barrel', -4.9, -6.2, 'orange'], ['barrel', -4.35, -6.75, 'orange'], ['barrel', -5.35, -7.05, 'red'],
    ['barrel', 7.2, 4.4, 'blue'], ['barrel', 7.75, 3.85, 'blue'],
    ['barrel', -10.9, 11.4, 'green'], ['crate_metal', 10.4, -12.0, 'yellow'],
    ['barrel', 12.4, -16.2, 'orange'], ['jerrycan', -8.4, -15.4, 'red'],
    ['crate_plastic', 3.9, 15.2, 'blue'], ['crate_plastic', 4.6, 15.5, 'white'],
  ];
  for (const [kind, ax, az, hue] of anchors) {
    w.props.place(kind, xform([ax, 0, az], rng.range(0, 6.28),
      [rng.range(0.94, 1.08), rng.range(0.94, 1.1), rng.range(0.94, 1.08)]), accentTint(hue, rng));
  }
  bollards(w, { a: [SQUARE.x1 - 1.1, -12], b: [SQUARE.x1 - 1.1, -4], count: 5 });
  bollards(w, { a: [SQUARE.x0 + 1.1, 4], b: [SQUARE.x0 + 1.1, 12], count: 5 });
}

/* ---- RIGHT LANE: the two-storey souk house -------------------------- */

function buildShop(w) {
  const rng = w.rng;
  const { x0, x1, z0, z1, f1, top, para } = SHOP;
  const T = 0.4, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;

  /* --- floors ---------------------------------------------------------- */
  groundPatch(w, { x0: x0 + 0.2, z0: z0 + 0.2, x1: x1 - 0.2, z1: z1 - 0.2, y: 0.02, mat: 'concrete_floor', surface: 'concrete', res: 0.7, uvScale: 1.6, tint: [0.94, 0.92, 0.88] });
  for (let i = 0; i < 9; i++) {
    driftPatch(w, { x: rng.range(x0 + 1, x1 - 1), z: rng.range(z0 + 1, z1 - 1), rx: rng.range(0.7, 2.2), rz: rng.range(0.7, 2.0), rot: rng.range(0, Math.PI), y: 0.03, mat: 'dirt', surface: 'dirt', tint: [0.86, 0.83, 0.78] });
  }
  slab(w, {
    x0: x0 - 0.05, z0: z0 - 0.05, x1: x1 + 0.05, z1: z1 + 0.05, y: f1, thickness: 0.34,
    mat: 'concrete_floor', uvScale: 2.0,
    holes: [{ x0: 24.55, z0: 2.9, x1: 27.55, z1: 7.95 }],
  });
  slab(w, {
    x0: x0 - 0.28, z0: z0 - 0.28, x1: x1 + 0.28, z1: z1 + 0.28, y: top, thickness: 0.36,
    mat: 'concrete_floor', uvScale: 2.0,
    holes: [{ x0: 24.6, z0: 8.1, x1: 27.5, z1: 13.4 }],
  });

  /* --- exterior walls, storey by storey -------------------------------- */
  // WEST facade (faces the square) — the shopfront and the first-floor firing windows
  wall(w, {
    a: [x0 + T / 2, z0], b: [x0 + T / 2, z1], base: 0, height: f1, thickness: T,
    mat: 'stucco', uvScale: 1.7, damage: 2, pilasters: [-11.4, -0.2, 11.4], reveal: true,
    plinth: { h: 0.42, proj: 0.07 },
    openings: [
      { x: -9.2, y: 0.9, w: 2.7, h: 2.0, glass: 'dirty', shutter: true },
      // middle shopfront blown out — the sightline the whole right lane is built on
      { x: -3.4, y: 0.9, w: 2.7, h: 2.0, shutter: true },
      { x: 1.8, y: 0, w: 1.5, h: 2.35, sill: false },
      { x: 7.6, y: 0.9, w: 2.5, h: 2.0, glass: 'dirty', shutter: true },
    ],
    band: { y: f1 - 0.18, h: 0.18, proj: 0.1 },
  });
  wall(w, {
    a: [x0 + T / 2, z0], b: [x0 + T / 2, z1], base: f1, height: top - f1, thickness: T,
    mat: 'stucco', uvScale: 1.7, damage: 2, plinth: false, reveal: true,
    cornice: { y: top - f1, proj: 0.26, h: 0.32 },
    openings: [
      { x: -10.5, y: 0.95, w: 1.25, h: 1.6, glass: 'dirty' },
      { x: -5.2, y: 0.95, w: 1.25, h: 1.6, arch: true, glass: 'dirty' },
      { x: 0.4, y: 0.95, w: 1.25, h: 1.6, arch: true },
      { x: 5.8, y: 0.95, w: 1.25, h: 1.6, arch: true, glass: 'dirty' },
      { x: 10.8, y: 0.95, w: 1.25, h: 1.6, glass: 'dirty' },
    ],
    parapet: { y: top - f1, h: para, thickness: 0.3 },
  });

  // EAST wall (map boundary side)
  wall(w, {
    a: [x1 - T / 2, z1], b: [x1 - T / 2, z0], base: 0, height: f1, thickness: T,
    mat: 'plaster_wall', uvScale: 1.7, damage: 2, reveal: true,
    openings: [
      { x: -8.0, y: 2.1, w: 1.0, h: 1.0, grille: true, glass: 'dirty' },
      { x: 0.5, y: 0, w: 1.4, h: 2.3, sill: false },
      { x: 8.5, y: 2.1, w: 1.0, h: 1.0, grille: true, glass: 'dirty' },
    ],
  });
  wall(w, {
    a: [x1 - T / 2, z1], b: [x1 - T / 2, z0], base: f1, height: top - f1, thickness: T,
    mat: 'plaster_wall', uvScale: 1.7, damage: 1, plinth: false,
    cornice: { y: top - f1, proj: 0.24, h: 0.3 },
    parapet: { y: top - f1, h: para, thickness: 0.3 },
    openings: [
      { x: -7.0, y: 1.0, w: 1.1, h: 1.5, glass: 'dirty' },
      { x: 2.0, y: 1.0, w: 1.1, h: 1.5, glass: 'dirty' },
      { x: 9.0, y: 1.0, w: 1.1, h: 1.5, glass: 'dirty' },
    ],
  });

  // NORTH wall
  for (const [base, h, ops] of [
    [0, f1, [{ x: -3.5, y: 0, w: 1.5, h: 2.3, arch: true, sill: false, lintel: false }, { x: 4.0, y: 1.0, w: 1.3, h: 1.5, glass: 'dirty', grille: true }]],
    [f1, top - f1, [{ x: -3.0, y: 1.0, w: 1.2, h: 1.55, glass: 'dirty' }, { x: 3.5, y: 1.0, w: 1.2, h: 1.55, arch: true, glass: 'dirty' }]],
  ]) {
    wall(w, {
      a: [x1, z0 + T / 2], b: [x0, z0 + T / 2], base, height: h, thickness: T,
      mat: 'plaster_wall', uvScale: 1.7, damage: 1, plinth: base === 0 ? { h: 0.42, proj: 0.07 } : false,
      openings: ops,
      cornice: base > 0 ? { y: h, proj: 0.24, h: 0.3 } : null,
      parapet: base > 0 ? { y: h, h: para, thickness: 0.3 } : null,
    });
  }
  // SOUTH wall (loading opening)
  for (const [base, h, ops] of [
    [0, f1, [{ x: -3.0, y: 0, w: 3.1, h: 2.7, sill: false }, { x: 4.5, y: 1.0, w: 1.3, h: 1.5, glass: 'dirty', grille: true }]],
    [f1, top - f1, [{ x: -3.5, y: 1.0, w: 1.2, h: 1.55, arch: true, glass: 'dirty' }, { x: 3.0, y: 1.0, w: 1.2, h: 1.55, glass: 'dirty' }]],
  ]) {
    wall(w, {
      a: [x0, z1 - T / 2], b: [x1, z1 - T / 2], base, height: h, thickness: T,
      mat: 'plaster_wall', uvScale: 1.7, damage: 1, plinth: base === 0 ? { h: 0.42, proj: 0.07 } : false,
      openings: ops,
      cornice: base > 0 ? { y: h, proj: 0.24, h: 0.3 } : null,
      parapet: base > 0 ? { y: h, h: para, thickness: 0.3 } : null,
    });
  }

  /* --- interior partitions --------------------------------------------- */
  // ground floor: shop | back room
  wall(w, {
    a: [22.2, z0 + 0.4], b: [22.2, z1 - 0.4], base: 0, height: f1 - 0.34, thickness: 0.26,
    mat: 'concrete_wall_painted', uvScale: 1.5, plinth: false, damage: 1,
    openings: [
      { x: -5.6, y: 0, w: 1.6, h: 2.25, arch: true, sill: false, lintel: false },
      { x: 4.2, y: 0.95, w: 2.2, h: 1.35, sill: true },
    ],
  });
  // back room / stair well divider
  wall(w, {
    a: [24.5, 2.9], b: [x1 - 0.4, 2.9], base: 0, height: f1 - 0.34, thickness: 0.24,
    mat: 'concrete_wall_painted', uvScale: 1.5, plinth: false,
    openings: [{ x: -0.55, y: 0, w: 2.4, h: 2.2, sill: false }],
  });
  // first floor partition
  wall(w, {
    a: [x0 + 0.4, 1.0], b: [22.3, 1.0], base: f1, height: top - f1 - 0.36, thickness: 0.24,
    mat: 'concrete_wall_painted', uvScale: 1.5, plinth: false, damage: 1,
    openings: [{ x: -1.4, y: 0, w: 1.5, h: 2.2, sill: false }, { x: 2.2, y: 1.1, w: 1.4, h: 1.2 }],
  });
  wall(w, {
    a: [22.2, z0 + 0.4], b: [22.2, 8.0], base: f1, height: top - f1 - 0.36, thickness: 0.24,
    mat: 'concrete_wall_painted', uvScale: 1.5, plinth: false,
    openings: [{ x: -3.0, y: 0, w: 1.5, h: 2.2, sill: false }],
  });

  /* --- stairs ---------------------------------------------------------- */
  stairs(w, { x: 26.05, z: 3.1, y: 0, rotY: 0, steps: 16, rise: f1 / 16, run: 0.30, width: 1.75, mat: 'concrete_floor', rail: 'left', stringer: 0 });
  stairs(w, { x: 26.05, z: 8.2, y: f1, rotY: 0, steps: 16, rise: (top - f1) / 16, run: 0.30, width: 1.75, mat: 'concrete_floor', rail: 'left', stringer: 0 });
  // stairwell edge guards
  railing(w, { a: [24.6, f1, 3.0], b: [24.6, f1, 8.6], height: 1.02, infill: 16 });
  railing(w, { a: [24.6, top, 8.2], b: [24.6, top, 13.3], height: 1.02, infill: 14 });
  // roof hatch housing
  {
    const g = chamferBox(3.1, 1.5, 1.2, 0.05);
    w.batch.add(g, 'plaster_wall', { matrix: xform([26.05, top + 0.75, 13.6], 0), surface: 'concrete', uvScale: 1.2 });
    g.dispose();
    w.blocker(26.05, top + 0.75, 13.6, 3.1, 1.5, 1.2, 0, 'concrete');
    const cap = chamferBox(3.4, 0.13, 1.5, 0.04);
    w.batch.add(cap, 'concrete_wall', { matrix: xform([26.05, top + 1.56, 13.6], 0), surface: 'concrete', uvScale: 0.7 });
    cap.dispose();
  }

  /* --- roof ------------------------------------------------------------ */
  roofClutter(w, { x0: x0 + 1.2, z0: z0 + 1.2, x1: x1 - 1.2, z1: 7.0, y: top, density: 1.15 });
  sandbagWall(w, { a: [x0 + 0.9, -7.0], b: [x0 + 0.9, -3.0], rows: 3, y: top });
  scatter(w, 'crate_wood_lg', { x0: x0 + 1.5, z0: z0 + 1.5, x1: x1 - 1.5, z1: 6, count: 5, y: top });
  scatter(w, 'tyre', { x0: x0 + 1.5, z0: z0 + 1.5, x1: x1 - 1.5, z1: 6, count: 4, y: top });
  laundryLine(w, [x0 + 1.2, top + 1.9, -10.5], [x1 - 1.2, top + 1.75, -8.0], { items: 4, sag: 0.4 });
  laundryLine(w, [x0 + 1.4, top + 1.8, 4.0], [x1 - 1.4, top + 1.95, 6.4], { items: 4, sag: 0.35 });
  for (let i = 0; i < 8; i++) {
    const k = rng.range(0.78, 1.06);
    driftPatch(w, { x: rng.range(x0 + 1, x1 - 1), z: rng.range(z0 + 1, z1 - 1), rx: rng.range(0.8, 2.4), rz: rng.range(0.8, 2.4), rot: rng.range(0, Math.PI), y: top + 0.012, mat: 'asphalt', surface: 'concrete', tint: [1.18 * k, 1.06 * k, 0.86 * k] });
  }
  crackScatter(w, { x0: x0 + 0.8, z0: z0 + 0.8, x1: x1 - 0.8, z1: z1 - 0.8, count: 14, y: top + 0.016, mat: 'asphalt' });
  for (let i = 0; i < 4; i++) {
    driftPatch(w, {
      x: rng.range(x0 + 1, x1 - 1), z: rng.range(z0 + 2, z1 - 2),
      rx: rng.range(0.9, 2.0), rz: rng.range(0.9, 2.2), rot: rng.range(0, Math.PI),
      y: top + 0.024, mat: 'concrete_floor', surface: 'concrete', tint: [0.60, 0.62, 0.66], seg: 24,
    });
  }
  for (let i = 0; i < 3; i++) {
    const g = quadXZ(rng.range(1.2, 2.6), rng.range(0.9, 1.9));
    w.batch.add(g, 'concrete_floor', {
      matrix: xform([rng.range(x0 + 1.4, x1 - 1.4), top + 0.03, rng.range(z0 + 2, z1 - 2)], rng.range(-0.25, 0.25)),
      surface: 'concrete', uvScale: 1.1, cast: false, tint: [1.22, 1.20, 1.16],
    });
    g.dispose();
  }
  scatter(w, 'barrel', { x0: x0 + 1.6, z0: z0 + 1.6, x1: x1 - 1.6, z1: 5, count: 4, y: top });
  scatter(w, 'pallet', { x0: x0 + 1.6, z0: z0 + 1.6, x1: x1 - 1.6, z1: 5, count: 3, y: top });
  scatter(w, 'cardboard', { x0: x0 + 1.6, z0: z0 + 1.6, x1: x1 - 1.6, z1: 5, count: 4, y: top });
  weeds(w, { x0: x0 + 0.8, z0: z0 + 0.8, x1: x1 - 0.8, z1: z1 - 0.8, count: 18, y: top });
  chimneyStack(w, { x: x1 - 2.2, y: top, z: -12.0, h: 1.7, wdt: 0.7 });
  /*
   * Roofline pass on the souk house. Its 1.05 m parapet is the top edge of the
   * whole right lane in `level_square`, `level_rooftop` and `wpn_rifle_hip`, and
   * it was a ruler-straight run of coping in all three.
   */
  rooflineClutter(w, {
    x0, z0, x1, z1, y: top, parapetH: para, density: 1.25, solid: true,
    edges: 'nsew', maxItems: 13,
  });

  /* --- shopfront dressing ---------------------------------------------- */
  for (const [z, wdt] of [[-9.2, 3.4], [-3.4, 3.4], [7.6, 3.2]]) {
    const c = AWNING[rng.int(0, AWNING.length - 1)], k = rng.range(0.86, 1.1);
    awning(w, { x: x0 - 0.02, z, y: 3.15, rotY: -Math.PI / 2, width: wdt, depth: 1.7, drop: 0.5, poles: true, tint: [c[0] * k, c[1] * k, c[2] * k] });
  }
  sign(w, { x: x0 - 0.16, y: 3.75, z: -6.2, rotY: -Math.PI / 2, width: 4.2, height: 0.82, color: 0xffd39a });
  sign(w, { x: x0 - 0.16, y: 3.7, z: 6.4, rotY: -Math.PI / 2, width: 3.0, height: 0.7, color: 0xa8d8ff });
  for (const z of [-12.4, -0.6, 11.6]) drainpipe(w, { x: x0 - 0.18, z, y0: 0.05, y1: top + 0.9, rotY: -Math.PI / 2 });
  for (const z of [-11.0, 2.4, 12.2]) {
    w.props.place('ac_wall_unit', xform([x0 - 0.22, 4.9, z], -Math.PI / 2));
  }
  conduit(w, [[x0 - 0.1, 3.3, -13], [x0 - 0.1, 3.3, 0], [x0 - 0.1, 4.0, 1.2], [x0 - 0.1, 4.0, 13]], { boxes: 2 });
  step(w, { x: x0 - 0.62, z: 1.8, rotY: 0, width: 1.3, depth: 2.3, h: 0.15 });
  scatter(w, 'crate_plastic', { x0: x0 - 2.0, z0: -12, x1: x0 - 0.6, z1: 12, count: 8 });
  scatter(w, 'produce_crate', { x0: x0 - 2.0, z0: -12, x1: x0 - 0.7, z1: 12, count: 6 });

  /* --- interior dressing + practicals ---------------------------------- */
  // shop counter
  {
    const g = chamferBox(3.4, 1.0, 0.7, 0.038);
    w.batch.add(g, 'wood_plank', { matrix: xform([19.4, 0.5, -7.6], 0.02), surface: 'wood', uvScale: 0.8 });
    g.dispose();
    const t = chamferBox(3.6, 0.075, 0.86, 0.028);
    w.batch.add(t, 'wood_plank', { matrix: xform([19.4, 1.03, -7.6], 0.02), surface: 'wood', uvScale: 0.6 });
    t.dispose();
    w.blocker(19.4, 0.5, -7.6, 3.6, 1.06, 0.86, 0.02, 'wood');
  }
  // shelving on the back wall
  for (let i = 0; i < 3; i++) {
    const g = chamferBox(0.42, 0.065, 5.4, 0.022);
    // indoor shelving flat against the back wall — nothing the sun cascades see
    w.batch.add(g, 'plywood', { matrix: xform([21.85, 0.7 + i * 0.75, -6.0], 0), surface: 'wood', uvScale: 0.5, cast: false });
    g.dispose();
    for (let k = 0; k < 6; k++) {
      w.props.place('crate_plastic', xform([21.85, 0.76 + i * 0.75, -8.4 + k * 0.95], rng.range(-0.1, 0.1), 0.75),
        variantTint('crate_plastic', rng));
    }
  }
  scatter(w, 'crate_wood', { x0: x0 + 1.0, z0: z0 + 1.0, x1: 21.8, z1: z1 - 1.0, count: 8 });
  scatter(w, 'crate_wood_lg', { x0: x0 + 1.0, z0: z0 + 1.0, x1: 21.8, z1: z1 - 1.0, count: 4 });
  scatter(w, 'produce_crate', { x0: x0 + 1.0, z0: z0 + 1.0, x1: 21.8, z1: z1 - 1.0, count: 7 });
  scatter(w, 'barrel', { x0: 22.6, z0: z0 + 1.0, x1: x1 - 1.0, z1: 2.0, count: 5 });
  scatter(w, 'ammo_box', { x0: 22.6, z0: z0 + 1.0, x1: x1 - 1.0, z1: 2.0, count: 6 });
  scatter(w, 'crate_metal', { x0: 22.6, z0: z0 + 1.0, x1: x1 - 1.0, z1: 2.0, count: 3 });
  scatter(w, 'chair', { x0: x0 + 1.2, z0: z0 + 1.2, x1: 21.6, z1: z1 - 1.2, count: 4 });
  w.props.place('table', xform([18.4, 0, 6.0], 0.3));
  // upstairs
  scatter(w, 'crate_wood', { x0: x0 + 1.0, z0: z0 + 1.0, x1: 21.8, z1: z1 - 1.0, count: 7, y: f1 });
  sandbagWall(w, { a: [x0 + 0.9, -6.0], b: [x0 + 0.9, -2.6], rows: 3, y: f1 });
  sandbagWall(w, { a: [x0 + 0.9, 4.2], b: [x0 + 0.9, 7.2], rows: 3, y: f1 });
  scatter(w, 'crate_plastic', { x0: 22.6, z0: z0 + 1, x1: x1 - 1, z1: 7, count: 5, y: f1 });
  scatter(w, 'cardboard', { x0: x0 + 1, z0: z0 + 1, x1: 21.8, z1: z1 - 1, count: 6, y: f1 });
  w.props.place('table', xform([17.6, f1, -9.0], -0.25));
  w.props.place('chair', xform([18.6, f1, -8.4], 2.1), variantTint('chair', rng));

  hangingBulb(w, { x: 18.2, y: f1 - 0.36, z: -8.6, drop: 0.75, intensity: 5.5, radius: 8 });
  hangingBulb(w, { x: 18.6, y: f1 - 0.36, z: 0.4, drop: 0.7, intensity: 5.0, radius: 8 });
  hangingBulb(w, { x: 19.0, y: f1 - 0.36, z: 8.4, drop: 0.8, intensity: 4.5, radius: 7.5 });
  hangingBulb(w, { x: 25.6, y: f1 - 0.36, z: -6.0, drop: 0.6, intensity: 3.6, radius: 7 });
  hangingBulb(w, { x: 18.4, y: top - 0.4, z: -6.0, drop: 0.7, intensity: 4.6, radius: 8 });
  hangingBulb(w, { x: 18.8, y: top - 0.4, z: 7.0, drop: 0.7, intensity: 4.0, radius: 7.5 });
  hangingBulb(w, { x: 26.0, y: top - 0.4, z: 5.6, drop: 0.5, intensity: 3.2, radius: 6 });
  w.light([18.5, 1.7, -6.0], 0xd8c6a8, 0.8, 12, 'room', { role: 'shop_fill' });
  w.light([25.8, 1.7, -6.0], 0xb8b0a0, 0.5, 9, 'room', { role: 'backroom_fill' });
  w.light([18.5, f1 + 1.7, 0.0], 0xd8c6a8, 0.7, 12, 'room', { role: 'upstairs_fill' });
  w.light([26.0, f1 + 1.7, 6.0], 0xb0aca4, 0.5, 8, 'room', { role: 'stairwell_fill' });

  // interior surface zones
  w.zone(x0, z0, x1, z1, 0.02, 'concrete');
  w.zone(x0, z0, x1, z1, f1, 'concrete');
  w.zone(x0, z0, x1, z1, top, 'concrete');
}

/* ---- perimeter ring + skyline --------------------------------------- */

function buildRing(w) {
  const rng = w.rng;
  const shopFront = (len) => {
    const o = windowRow(len, { w: 2.2, h: 2.1, y: 0.75, gap: 4.6, glass: 'dirty' });
    if (o.length > 1) o[1] = { x: o[1].x, y: 0, w: 1.3, h: 2.3, arch: true, sill: false, lintel: false };
    return o;
  };
  /*
   * F1b — the north and south ring rows are the only masses that can stand
   * between a sun on the map's long axis (Z) and the market square. At the
   * 32–42° elevation the lighting team is moving to, a block of height h at
   * z = ±34 throws its shadow h/tan(elev) into the map: a 4-storey (16 m) row
   * reaches z ≈ ±13 at 32° and puts the whole square in shade, which is exactly
   * the mistake F1b describes.
   *
   * So the rows are stepped: LOW (2 storeys, ~7.7 m to the coping) directly
   * over the centre lane, taller only on the flanks. 7.7 m at 32° reaches
   * z ≈ ±22 — short of the square's z = ±17 edge with 5 m to spare — while the
   * flanking 3-storey blocks keep the roofline from reading as a flat wall.
   * The skyline massing beyond the ring supplies the tall silhouette.
   */
  const CENTRE_LANE = [-15, 17];        // x span that must stay open to the sun
  const overCentre = (a, b) => b > CENTRE_LANE[0] && a < CENTRE_LANE[1];
  // north edge
  const north = [[-36, -14], [-14, 2], [2, 18], [18, 34]];
  for (const [a, b] of north) {
    const low = overCentre(a, b);
    blockBuilding(w, {
      x0: a, z0: -44, x1: b, z1: -34,
      storeys: low ? 2 : rng.int(2, 3), storeyH: low ? rng.range(3.1, 3.3) : rng.range(3.2, 3.5),
      faces: { s: true, e: b === 34, w: a === -36 }, balconies: rng.int(1, 2), groundOpenings: shopFront,
    });
  }
  // south edge
  const south = [[-36, -18], [-18, -2], [-2, 16], [16, 34]];
  for (const [a, b] of south) {
    const low = overCentre(a, b);
    blockBuilding(w, {
      x0: a, z0: 34, x1: b, z1: 44,
      storeys: low ? 2 : rng.int(2, 3), storeyH: low ? rng.range(3.1, 3.3) : rng.range(3.2, 3.5),
      faces: { n: true }, balconies: rng.int(1, 2), groundOpenings: shopFront,
    });
  }
  // east edge — beyond the souk house
  for (const [a, b] of [[-34, -14], [-14, 6], [6, 24], [24, 34]]) {
    blockBuilding(w, {
      x0: 30, z0: a, x1: 41, z1: b, storeys: rng.int(2, 4), storeyH: rng.range(3.2, 3.9),
      faces: { w: true }, balconies: 1, groundOpenings: shopFront,
    });
  }
  // west edge, behind block A
  for (const [a, b] of [[-34, -8], [-8, 14], [14, 34]]) {
    blockBuilding(w, {
      x0: -48, z0: a, x1: -36, z1: b, storeys: rng.int(3, 5), storeyH: rng.range(3.1, 3.6),
      faces: {}, clutter: true,
    });
  }

  // buildings closing the ends of the right lane
  blockBuilding(w, {
    x0: 16, z0: -34, x1: 30, z1: -23.5, storeys: 2, storeyH: 3.6,
    faces: { s: true, w: true }, groundOpenings: shopFront, balconies: 1,
  });
  blockBuilding(w, {
    x0: 16, z0: 23.5, x1: 30, z1: 34, storeys: 2, storeyH: 3.7,
    faces: { n: true, w: true }, groundOpenings: shopFront, balconies: 1,
  });

  // colonnade along the south of the square — shade, cover, and depth
  colonnade(w, { x: 1.0, z: 19.2, rotY: 0, bays: 5, bay: 3.1, height: 3.1, pierW: 0.62, pierD: 0.72, mat: 'stucco' });

  // overhead power runs so the sky is never empty
  powerRun(w, [
    [-25.4, 9.4, -30], [-25.4, 9.0, -10], [-13.4, 5.0, 2], [15.2, 8.2, 4], [29.6, 8.4, -6],
  ], { count: 3, sag: 1.1 });
  powerRun(w, [[-13.4, 5.0, -24], [15.2, 8.2, -20], [30.4, 8.6, -18]], { count: 2, sag: 1.3 });
  powerRun(w, [[-13.4, 5.0, 14], [15.2, 8.2, 16], [30.4, 8.6, 20]], { count: 2, sag: 1.2 });
}

function buildSkyline(w) {
  const rng = w.rng;
  const FAR = { cast: false, recv: false, uvScale: 5 };
  /**
   * A stepped mass: base block, floor bands, a set-back upper storey, a stair
   * penthouse and roof furniture.
   *
   * The bands are the whole point. `level_overview` (1230–1920, 0–180) read as
   * "untextured white placeholder cubes" because that is exactly what a
   * chamfered box with one flat tint is at 200 m. Three or four dark horizontal
   * strings per block resolve into floor lines at that distance for one extra
   * merged piece each, and the near ring gets tanks and masts so the skyline
   * silhouette is not a row of flat tops.
   */
  const massing = (x, z, wdt, dep, h, tint, rot, ring) => {
    const g = chamferBox(wdt, h, dep, 0.22);
    w.batch.add(g, 'concrete_wall', { matrix: xform([x, h / 2 - 1.4, z], rot), surface: 'concrete', tint, ...FAR });
    g.dispose();
    // floor bands: a proud dark string course every ~3.2 m of height
    const floors = Math.max(1, Math.min(4, Math.floor(h / 3.6)));
    for (let f = 1; f <= floors; f++) {
      const by = h * (f / (floors + 1)) - 1.4;
      const dark = f % 2 === 1;
      const band = chamferBox(wdt + 0.28, dark ? 1.05 : 0.42, dep + 0.28, 0.12);
      w.batch.add(band, 'concrete_wall', {
        matrix: xform([x, by, z], rot), surface: 'concrete',
        tint: dark
          ? [tint[0] * 0.44, tint[1] * 0.45, tint[2] * 0.52]
          : [tint[0] * 1.12, tint[1] * 1.10, tint[2] * 1.05],
        ...FAR,
      });
      band.dispose();
    }
    /*
     * Parapet, stepped along each of the four top edges.
     *
     * The old version was one `chamferBox(wdt+0.5, 0.9, dep+0.5)` cap, i.e. a
     * flat lid, and that is precisely why `level_square` (700–1200, 400–450) read
     * as "untextured boxes": a box with a flat top has one horizontal silhouette
     * edge and nothing else to look at. Four independent stepped runs give the
     * block a broken top for the price of ~10 merged boxes in a group that
     * already exists.
     */
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const at = (u, v) => [x + cos * u - sin * v, z + sin * u + cos * v];
    const pTop = h - 1.4;
    const edgeRun = (alongW, fixedV, isU) => {
      const L = alongW;
      const segs = rng.int(2, 4);
      let cur = -L / 2, prev = null;
      for (let s = 0; s < segs; s++) {
        const want = s === segs - 1 ? (L / 2 - cur) : (L / segs) * rng.range(0.6, 1.5);
        const sl = Math.min(want, L / 2 - cur);
        if (sl < 0.8) break;
        const mid = cur + sl / 2;
        cur += sl;
        let ph = rng.range(0.5, 2.6);
        if (prev !== null && Math.abs(ph - prev) < 0.4) ph = Math.max(0.4, prev + rng.sign() * rng.range(0.5, 1.2));
        prev = ph;
        const p = isU ? at(mid, fixedV) : at(fixedV, mid);
        const g = isU ? chamferBox(sl, ph, 0.7, 0.12) : chamferBox(0.7, ph, sl, 0.12);
        w.batch.add(g, 'concrete_wall', {
          matrix: xform([p[0], pTop + ph / 2, p[1]], rot), surface: 'concrete',
          tint: [tint[0] * 1.06, tint[1] * 1.05, tint[2] * 1.02], ...FAR,
        });
        g.dispose();
      }
    };
    edgeRun(wdt + 0.5, -dep / 2 - 0.15, true);
    edgeRun(wdt + 0.5, dep / 2 + 0.15, true);
    edgeRun(dep + 0.5, -wdt / 2 - 0.15, false);
    edgeRun(dep + 0.5, wdt / 2 + 0.15, false);
    if (rng() < 0.62) {                        // set-back upper storey
      const uh = rng.range(2.5, 7);
      const uw = wdt * rng.range(0.35, 0.72), ud = dep * rng.range(0.35, 0.72);
      const ox = rng.range(-1, 1) * (wdt - uw) * 0.4, oz = rng.range(-1, 1) * (dep - ud) * 0.4;
      const u = chamferBox(uw, uh, ud, 0.18);
      w.batch.add(u, 'concrete_wall', { matrix: xform([x + ox, h - 1.4 + uh / 2, z + oz], rot), surface: 'concrete', tint, ...FAR });
      u.dispose();
    }
    if (rng() < 0.5) {                         // stair penthouse / tank block
      const ph = rng.range(1.6, 3.2);
      const p = chamferBox(rng.range(2, 4.5), ph, rng.range(2, 4.5), 0.14);
      w.batch.add(p, 'concrete_wall', {
        matrix: xform([x + rng.range(-1, 1) * wdt * 0.28, h - 1.4 + ph / 2, z + rng.range(-1, 1) * dep * 0.28], rot),
        surface: 'concrete', tint: [tint[0] * 0.95, tint[1] * 0.94, tint[2] * 0.93], ...FAR,
      });
      p.dispose();
    }
    /*
     * Roof furniture out to ring 2 (was ring 1).
     *
     * At the 74–78° FOV these shots use, ring 2 sits 140–215 m out and still
     * subtends 8–15 px per metre of mast, so a 6 m aerial is a 60 px vertical —
     * far from invisible. Masts are also much thinner than they were: a 30 cm
     * square post at 80 m is 4 px and reads as a smear, an 8–12 cm one reads as
     * a wire, which is what an aerial actually looks like.
     */
    if (ring <= 2) {
      const n = rng.int(3, 7);
      for (let i = 0; i < n; i++) {
        const px = x + rng.range(-0.38, 0.38) * wdt, pz = z + rng.range(-0.38, 0.38) * dep;
        const r2 = rng();
        if (r2 < 0.42) {                       // slim aerial mast with a crossarm
          const mh = rng.range(3.5, 11.0);
          const mw = rng.range(0.09, 0.17);
          const m2 = chamferBox(mw, mh, mw, 0.02);
          w.batch.add(m2, 'concrete_wall', { matrix: xform([px, pTop + mh / 2, pz], rot), surface: 'concrete', tint: [tint[0] * 0.5, tint[1] * 0.5, tint[2] * 0.56], ...FAR });
          m2.dispose();
          const arm = chamferBox(rng.range(0.8, 1.9), 0.07, 0.07, 0.015);
          w.batch.add(arm, 'concrete_wall', { matrix: xform([px, pTop + mh * rng.range(0.6, 0.9), pz], rot + rng.range(0, 3.1)), surface: 'concrete', tint: [tint[0] * 0.5, tint[1] * 0.5, tint[2] * 0.56], ...FAR });
          arm.dispose();
        } else if (r2 < 0.62) {                // tank on a stand — reads as a lollipop
          const th = rng.range(1.2, 2.4), tr = rng.range(0.7, 1.5);
          const stand = rng.range(1.0, 2.6);
          const body = chamferCyl(tr, th, 8, 0.1);
          w.batch.add(body, 'concrete_wall', { matrix: xform([px, pTop + stand + th / 2, pz], rot), surface: 'concrete', tint: [tint[0] * 0.9, tint[1] * 0.88, tint[2] * 0.9], ...FAR });
          body.dispose();
          for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
            const leg = chamferBox(0.11, stand, 0.11, 0.02);
            w.batch.add(leg, 'concrete_wall', { matrix: xform([px + dx * tr * 0.6, pTop + stand / 2, pz + dz * tr * 0.6], rot), surface: 'concrete', tint: [tint[0] * 0.55, tint[1] * 0.55, tint[2] * 0.6], ...FAR });
            leg.dispose();
          }
        } else {                               // plant box / stair head
          const bw = rng.range(1.2, 3.4), bh = rng.range(0.9, 3.2);
          const b2 = chamferBox(bw, bh, rng.range(1.2, 3.0), 0.1);
          w.batch.add(b2, 'concrete_wall', { matrix: xform([px, pTop + bh / 2, pz], rot + rng.range(-0.6, 0.6)), surface: 'concrete', tint: [tint[0] * 0.82, tint[1] * 0.83, tint[2] * 0.88], ...FAR });
          b2.dispose();
        }
      }
    }
  };

  const bands = [
    { r0: 60, r1: 86, n: 40, h0: 5, h1: 15 },
    { r0: 88, r1: 135, n: 54, h0: 6, h1: 22 },
    { r0: 140, r1: 215, n: 62, h0: 7, h1: 28 },
    { r0: 220, r1: 320, n: 56, h0: 8, h1: 34 },
  ];
  bands.forEach((b, ring) => {
    // real aerial perspective: distant massing loses contrast and cools off,
    // instead of every ring sitting at the same near-white value
    const fade = 1 - ring * 0.10;
    const cool = 1 + ring * 0.09;
    for (let i = 0; i < b.n; i++) {
      const a = (i / b.n) * Math.PI * 2 + rng.range(-0.06, 0.06);
      const r = rng.range(b.r0, b.r1);
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (Math.abs(x) < 50 && Math.abs(z) < 50) continue;   // never inside the town
      const h = rng.range(b.h0, b.h1);
      const s = 1 + ring * 0.5;
      const warm = rng.range(0.86, 1.08);
      massing(x, z, rng.range(8, 18) * s, rng.range(8, 18) * s, h,
        [0.82 * fade * warm, 0.79 * fade * warm, 0.74 * fade * cool], rng.range(0, Math.PI), ring);
    }
  });

  /*
   * The plain between the town and the ridge read as "a flat sand plane". Low
   * dunes and scrub break the horizon line without a single new draw call —
   * every piece lands in the `dirt`/`sand` far groups that already exist.
   */
  for (let i = 0; i < 70; i++) {
    const a = rng.range(0, Math.PI * 2), r = rng.range(56, 380);
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    if (Math.abs(x) < 48 && Math.abs(z) < 48) continue;
    const dh = rng.range(1.4, 6.0);
    const g = chamferBox(rng.range(18, 60), dh, rng.range(12, 40), 1.6);
    w.batch.add(g, 'dirt', {
      matrix: xform([x, dh / 2 - dh * 0.62, z], a + rng.range(-0.6, 0.6)),
      surface: 'dirt', uvScale: 14, tint: [rng.range(0.86, 1.04), rng.range(0.84, 1.0), rng.range(0.76, 0.92)],
      cast: false, recv: false,
    });
    g.dispose();
  }

  // minarets, water towers and a dome — the shapes that say "this is not a generic city"
  for (let i = 0; i < 11; i++) {
    const a = rng.range(0, Math.PI * 2), r = rng.range(66, 240);
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    if (Math.abs(x) < 50 && Math.abs(z) < 50) continue;
    const h = rng.range(20, 40);
    const rr = rng.range(1.3, 2.3);
    const shaft = chamferCyl(rr, h, 10, 0.18);
    w.batch.add(shaft, 'concrete_wall', { matrix: xform([x, h / 2 - 1, z], 0), surface: 'concrete', tint: [0.94, 0.91, 0.84], ...FAR });
    shaft.dispose();
    const gal = chamferCyl(rr * 1.7, 1.3, 10, 0.15);
    w.batch.add(gal, 'concrete_wall', { matrix: xform([x, h * 0.76, z], 0), surface: 'concrete', tint: [0.98, 0.95, 0.88], ...FAR });
    gal.dispose();
    const upper = chamferCyl(rr * 0.8, h * 0.2, 10, 0.12);
    w.batch.add(upper, 'concrete_wall', { matrix: xform([x, h * 0.86, z], 0), surface: 'concrete', tint: [0.94, 0.91, 0.84], ...FAR });
    upper.dispose();
    const cap = taperCyl(rr * 1.1, 0.1, 4.2, 10);
    w.batch.add(cap, 'concrete_wall', { matrix: xform([x, h * 0.96 + 2.1, z], 0), surface: 'concrete', tint: [0.72, 0.7, 0.66], ...FAR });
    cap.dispose();
  }
  for (let i = 0; i < 5; i++) {
    const a = rng.range(0, Math.PI * 2), r = rng.range(70, 200);
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    if (Math.abs(x) < 50 && Math.abs(z) < 50) continue;
    const dr = rng.range(4, 8);
    const dome = stripAttrs(new THREE.SphereGeometry(dr, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2).toNonIndexed());
    w.batch.add(dome, 'concrete_wall', {
      matrix: xform([x, rng.range(7, 16), z], 0), surface: 'concrete',
      tint: [0.9, 0.88, 0.82], ...FAR,
    });
    dome.dispose();
  }

  // distant coastal ridge so the horizon reads as land, not void
  for (let i = 0; i < 34; i++) {
    const a = (i / 34) * Math.PI * 2 + rng.range(-0.04, 0.04);
    const r = 430 + rng.range(-70, 70);
    const h = rng.range(26, 70);
    const g = chamferBox(rng.range(110, 220), h, 70, 4);
    w.batch.add(g, 'dirt', {
      matrix: xform([Math.cos(a) * r, h / 2 - 26, Math.sin(a) * r], a + rng.range(-0.3, 0.3)),
      surface: 'dirt', uvScale: 22, tint: [0.74, 0.71, 0.66], cast: false, recv: false,
    });
    g.dispose();
  }
}

/* ---- the silhouette pass -------------------------------------------- */

/**
 * Everything that exists purely to break the line where geometry meets sky.
 * Runs LAST, because `linkCables` consumes the anchors every earlier pass
 * registered.
 *
 * The brief: "every dev frame has a ruler-straight roofline". The reference
 * frames say what the cure is — `refs/mw3_06.jpg` has an open lattice tower at
 * (1700, 300), four parallel catenaries across the whole sky, floodlight
 * brackets off every gantry and a mast breaking the cloud at (1180, 100);
 * `refs/mw3_04.jpg` cuts its terminal roof with a light mast at (1160, 20) and
 * a red truss run. None of it is expensive. All of it is vertical.
 */
function buildSilhouette(w) {
  const rng = w.rng;

  /*
   * Lattice towers. The one shape in the kit the sky reads *through*, so it is
   * worth its ~5 k triangles. Sited on the two axes the hero shots look down:
   * `level_square` / `wpn_rifle_hip` / `light_goldenhour` look north (−Z),
   * `level_rooftop` looks east (+X).
   */
  const towers = [
    { x: -8, z: -74, h: 26, base: 3.4, bays: 8 },
    { x: 26, z: -58, h: 19, base: 2.6, bays: 7 },
    { x: 62, z: -14, h: 23, base: 3.0, bays: 8 },
    { x: 78, z: 40, h: 17, base: 2.4, bays: 6 },
    { x: -46, z: 62, h: 21, base: 2.8, bays: 7 },
    { x: -84, z: -30, h: 29, base: 3.8, bays: 9 },
  ];
  for (const t of towers) {
    latticeTower(w, {
      x: t.x, y: rng.range(-0.4, 0.6), z: t.z, h: t.h, base: t.base, bays: t.bays,
      taper: rng.range(0.32, 0.5), rotY: rng.range(0, Math.PI / 2), far: true,
      // `metal_rusted` is a saturated red-brown. Left alone a 26 m tower at 90 m
      // reads as an orange stripe against the cloud; the reference towers in
      // `refs/mw3_06.jpg` are galvanised steel gone sandy grey, so the tint pulls
      // green and blue up to neutralise the rust rather than dimming the whole
      // thing (dimming would just make it a dark orange stripe).
      tint: [rng.range(0.92, 1.05), rng.range(1.14, 1.30), rng.range(1.38, 1.62)],
    });
  }

  /*
   * Industrial stacks. A tapered 30 m chimney is one of the few silhouettes that
   * reads instantly at 120 m, and it puts a strong vertical in the band the
   * skyline massing leaves flat.
   */
  for (const [sx, sz, sh, sr] of [[-30, -96, 34, 1.7], [44, -84, 27, 1.4], [96, 26, 30, 1.6]]) {
    const stack = taperCyl(sr, sr * 0.62, sh, 10);
    w.batch.add(stack, 'concrete_wall', {
      matrix: xform([sx, sh / 2 - 1, sz], 0), surface: 'concrete',
      tint: [0.88, 0.85, 0.79], cast: false, recv: false, uvScale: 5,
    });
    stack.dispose();
    for (let b = 0; b < 3; b++) {
      const band = chamferCyl(sr * (0.9 - b * 0.06), 1.1, 10, 0.1);
      w.batch.add(band, 'concrete_wall', {
        matrix: xform([sx, sh * (0.35 + b * 0.22), sz], 0), surface: 'concrete',
        tint: [0.5, 0.44, 0.42], cast: false, recv: false, uvScale: 5,
      });
      band.dispose();
    }
  }

  /*
   * Service poles on the ground, standing in the streets and courtyards. They
   * are the anchors that give the catenary web its low, foreground spans — the
   * cables in `refs/mw3_06.jpg` that pass in front of the sky rather than along
   * the roofline.
   */
  /*
   * Sited in the streets and courtyards, NOT inside a mass. Every one of these
   * was checked against the block footprints by hand — block A is x ≤ −26,
   * block B is x −20…−13.5 over z −26…16, the souk is x 15…29, the east ring
   * starts at x 30, and the two lane-end blocks are x 16…30 at |z| > 23.5.
   * An 8 m pole planted inside a 7 m building pokes out of its roof, which is
   * the same class of mistake as a stage buried in a hill (FINDINGS F19).
   */
  for (const [px, pz, ph] of [
    [-23.0, -20.0, 8.2], [-23.0, 19.6, 7.6], [-23.0, -27.5, 7.4], [-23.0, 27.0, 7.2],
    [-10.0, -20.4, 7.8], [10.4, -20.0, 8.1], [10.0, 19.8, 7.5],
    [-13.0, -31.0, 7.0], [12.6, -31.0, 7.3], [-13.4, 31.0, 6.9], [12.4, 31.2, 7.2],
    [13.9, -12.2, 6.4], [13.9, 10.6, 6.1],
  ]) {
    cablePole(w, { x: px, y: 0, z: pz, h: ph, rotY: rng.range(0, Math.PI), arms: 2 });
    w.blocker(px, ph / 2, pz, 0.2, ph, 0.2, 0, 'metal', false);
  }

  /*
   * The web. Greedy nearest-neighbour between every pole head planted by every
   * roofline pass in the map, which is ~70 anchors across ~20 buildings. Two
   * cables per span, `cast:false` — a 1.5 cm wire's shadow is noise, and the
   * cables land in a batch group that already exists.
   */
  const spans = linkCables(w, { min: 9, max: 30, fan: 2, sag: 0.85, bundle: 2 });
  return { towers: towers.length, spans, anchors: w.cableAnchors.length };
}

/* ---- overhead-support self-test (N2) -------------------------------- */

/*
 * "Re-seat every awning prop onto its stall" was reported done once and was not
 * done. This is the check that makes that impossible to repeat: it does not
 * consult the record of where a support was *authored*, it looks at the
 * triangles that actually got merged into the level and asks whether anything
 * is under each canopy. If a canopy is deleted, moved, or its poles are dropped,
 * the count goes non-zero on the next boot.
 *
 * It is deliberately not THREE.Raycaster: the level is 29 merged meshes with
 * ~600 k triangles and no BVH, so a Raycaster sweep of ~8 000 probe rays takes
 * tens of seconds. Instead the triangles near the canopies are gathered once
 * into a 1 m XZ hash, and a downward probe is a point-in-triangle test in XZ.
 */

const SUP_CELL = 1.0;
const key2 = (x, z) => `${Math.floor(x / SUP_CELL)},${Math.floor(z / SUP_CELL)}`;

/** Gather the level's *rendered* triangles in the neighbourhood of `probes`. */
function collectSupportTris(root, probes, pad = 2.6, below = 5.0) {
  const mask = new Set();
  let yLo = Infinity, yHi = -Infinity;
  const R = Math.ceil(pad / SUP_CELL);
  for (const p of probes) {
    yLo = Math.min(yLo, p[1]); yHi = Math.max(yHi, p[1]);
    const ci = Math.floor(p[0] / SUP_CELL), cj = Math.floor(p[2] / SUP_CELL);
    for (let i = -R; i <= R; i++) for (let j = -R; j <= R; j++) mask.add(`${ci + i},${cj + j}`);
  }
  const lo = yLo - below, hi = yHi + 1.0;
  const tris = [];
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();
  const emit = (pos, mtx) => {
    const n = pos.count;
    for (let i = 0; i + 2 < n; i += 3) {
      let inside = false, tlo = Infinity, thi = -Infinity;
      const t = new Float32Array(9);
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, i + k);
        if (mtx) v.applyMatrix4(mtx);
        t[k * 3] = v.x; t[k * 3 + 1] = v.y; t[k * 3 + 2] = v.z;
        if (v.y < tlo) tlo = v.y;
        if (v.y > thi) thi = v.y;
        if (!inside && mask.has(key2(v.x, v.z))) inside = true;
      }
      if (inside && thi >= lo && tlo <= hi) tris.push(t);
    }
  };
  root.traverse((o) => {
    if (!o.isMesh || !o.visible || o.userData.collisionProxy) return;
    const pos = o.geometry?.attributes?.position;
    if (!pos) return;
    if (o.isInstancedMesh) {
      if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere();
      const bs = o.geometry.boundingSphere;
      for (let i = 0; i < o.count; i++) {
        o.getMatrixAt(i, m);
        m.premultiply(o.matrixWorld);
        v.copy(bs.center).applyMatrix4(m);
        const sc = Math.max(
          Math.hypot(m.elements[0], m.elements[1], m.elements[2]),
          Math.hypot(m.elements[4], m.elements[5], m.elements[6]),
          Math.hypot(m.elements[8], m.elements[9], m.elements[10]));
        const rad = bs.radius * sc;
        if (v.y - rad > hi || v.y + rad < lo) continue;
        // cheap XZ reject on the instance's own bounding circle
        let near = false;
        const step = Math.max(SUP_CELL, rad);
        for (let dx = -rad; dx <= rad + 1e-3 && !near; dx += step) {
          for (let dz = -rad; dz <= rad + 1e-3 && !near; dz += step) {
            if (mask.has(key2(v.x + dx, v.z + dz))) near = true;
          }
        }
        if (!near) continue;
        emit(pos, m);
      }
    } else {
      const mtx = o.matrixWorld.equals(_IDENT) ? null : o.matrixWorld;
      emit(pos, mtx);
    }
  });
  // bucket into a 1 m XZ hash so a probe touches ~10 triangles, not ~5000
  const grid = new Map();
  for (const t of tris) {
    const x0 = Math.min(t[0], t[3], t[6]), x1 = Math.max(t[0], t[3], t[6]);
    const z0 = Math.min(t[2], t[5], t[8]), z1 = Math.max(t[2], t[5], t[8]);
    for (let i = Math.floor(x0 / SUP_CELL); i <= Math.floor(x1 / SUP_CELL); i++) {
      for (let j = Math.floor(z0 / SUP_CELL); j <= Math.floor(z1 / SUP_CELL); j++) {
        const k = `${i},${j}`;
        let list = grid.get(k);
        if (!list) { list = []; grid.set(k, list); }
        list.push(t);
      }
    }
  }
  return { tris, grid };
}

const _IDENT = new THREE.Matrix4();

/** Is there a triangle within `drop` metres straight down from (x,y,z)? */
function probeDown(acc, x, y, z, drop) {
  const list = acc.grid.get(key2(x, z));
  if (!list) return false;
  for (const t of list) {
    const ax = t[0], az = t[2], bx = t[3], bz = t[5], cx = t[6], cz = t[8];
    const d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
    if (Math.abs(d) < 1e-9) continue;
    const l1 = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / d;
    if (l1 < -1e-4 || l1 > 1.0001) continue;
    const l2 = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / d;
    if (l2 < -1e-4 || l2 > 1.0001) continue;
    const l3 = 1 - l1 - l2;
    if (l3 < -1e-4) continue;
    const hy = l1 * t[1] + l2 * t[4] + l3 * t[7];
    if (hy <= y + 0.01 && hy >= y - drop) return true;
  }
  return false;
}

/** Möller–Trumbore, used only for the handful of horizontal wall probes. */
function probeRay(acc, o, dir, far) {
  for (const t of acc.tris) {
    const e1 = [t[3] - t[0], t[4] - t[1], t[5] - t[2]];
    const e2 = [t[6] - t[0], t[7] - t[1], t[8] - t[2]];
    const p = [dir[1] * e2[2] - dir[2] * e2[1], dir[2] * e2[0] - dir[0] * e2[2], dir[0] * e2[1] - dir[1] * e2[0]];
    const det = e1[0] * p[0] + e1[1] * p[1] + e1[2] * p[2];
    if (Math.abs(det) < 1e-9) continue;
    const inv = 1 / det;
    const s = [o[0] - t[0], o[1] - t[1], o[2] - t[2]];
    const u = (s[0] * p[0] + s[1] * p[1] + s[2] * p[2]) * inv;
    if (u < -1e-5 || u > 1.00001) continue;
    const q = [s[1] * e1[2] - s[2] * e1[1], s[2] * e1[0] - s[0] * e1[2], s[0] * e1[1] - s[1] * e1[0]];
    const vv = (dir[0] * q[0] + dir[1] * q[1] + dir[2] * q[2]) * inv;
    if (vv < -1e-5 || u + vv > 1.00001) continue;
    const tt = (e2[0] * q[0] + e2[1] * q[1] + e2[2] * q[2]) * inv;
    if (tt > 1e-4 && tt <= far) return true;
  }
  return false;
}

const _LERP = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/**
 * For every registered awning / parasol / canopy: raycast straight down from a
 * 5x5 grid over the panel and require real geometry beneath it, or a wall the
 * panel is keyed into. Returns `{ total, failed, worst, details }` — `failed`
 * must be 0.
 */
function verifyOverheads(root, overheads) {
  const items = overheads.map((oh) => {
    const [c0, c1, c2, c3] = oh.corners;
    const samples = [];
    const N = 4;
    for (let i = 0; i <= N; i++) {
      for (let j = 0; j <= N; j++) {
        samples.push(_LERP(_LERP(c0, c1, i / N), _LERP(c3, c2, i / N), j / N));
      }
    }
    return { oh, samples };
  });
  if (!items.length) return { total: 0, failed: 0, worst: 0, details: [] };
  const probes = [];
  for (const it of items) probes.push(...it.samples);
  const acc = collectSupportTris(root, probes);
  // a support 14 cm to one side is still a support: the pole under an awning
  // corner sits 8 cm inboard of the cloth edge
  const OFF = [[0, 0], [0.14, 0], [-0.14, 0], [0, 0.14], [0, -0.14], [0.1, 0.1], [-0.1, -0.1], [0.1, -0.1], [-0.1, 0.1]];
  const details = [];
  let failed = 0, worstAll = 0;
  for (const it of items) {
    const anchors = [];
    for (const p of it.samples) {
      for (const [dx, dz] of OFF) {
        if (probeDown(acc, p[0] + dx, p[1], p[2] + dz, it.oh.drop)) { anchors.push(p); break; }
      }
    }
    if (it.oh.root) {
      const r = it.oh.root;
      if (probeRay(acc, r.p, r.dir, 0.5)) anchors.push(r.p);
    }
    let worst = Infinity;
    if (anchors.length) {
      worst = 0;
      for (const p of it.samples) {
        let best = Infinity;
        for (const a of anchors) {
          const d = Math.hypot(p[0] - a[0], p[1] - a[1], p[2] - a[2]);
          if (d < best) best = d;
        }
        if (best > worst) worst = best;
      }
    }
    const ok = anchors.length > 0 && worst <= it.oh.reach;
    if (!ok) failed++;
    if (Number.isFinite(worst)) worstAll = Math.max(worstAll, worst);
    details.push({
      kind: it.oh.kind, ok, anchors: anchors.length,
      worstGap: Number.isFinite(worst) ? +worst.toFixed(3) : null,
      reach: it.oh.reach,
      at: it.samples[12].map((n) => +n.toFixed(2)),
    });
  }
  return { total: items.length, failed, worst: +worstAll.toFixed(3), details };
}

/* ---- lighting hand-off --------------------------------------------- */

/**
 * F1b hand-off to the lighting team, emitted LAST on purpose.
 *
 * The square is built so a 32–42° sun on the map's long axis lands on it. The
 * two souk-canopy bands sit at z = -7.4 and z = +5.6, so the strip between them
 * (z ≈ -5 … +4, centred on the dry fountain) has open sky above it — that is
 * the sunlit ground to fight over. The souk house (x = 15, 8.2 m to the coping)
 * shades a 10–13 m band to its west at those elevations: the side to hold.
 *
 * These are `type: 'probe'` hints, which `Lighting.buildPracticals` skips and
 * `Env.collectProbeSites` picks up for GI. Env keeps only the first six probe
 * sites, so they are published after every interior `room` hint — an outdoor
 * marker must never displace the shop's interior bounce probe. Intensity stays
 * at 1.0: Env multiplies `envMapIntensity` by it, so a 0 here would switch the
 * environment map off for everything the probe covers.
 */
function buildLightMarkers(w) {
  w.light([0.5, 1.75, -1.0], 0xfff0d8, 1.0, 10, 'probe', { role: 'sun_patch', note: 'open sky, centre of the square' });
  w.light([12.6, 1.75, -6.0], 0xbcd0e4, 1.0, 9, 'probe', { role: 'shade_pocket', note: 'west pavement under the souk house' });
  w.light([-2.0, 1.75, 11.0], 0xfff0d8, 1.0, 10, 'probe', { role: 'sun_patch', note: 'open sky, south of the canopies' });
  w.light([-9.6, 1.75, -7.4], 0xbcd0e4, 1.0, 9, 'probe', { role: 'shade_pocket', note: 'under the souk canopy' });
}

/* ---- spawn-area dressing ------------------------------------------- */

function buildSpawns(w) {
  const rng = w.rng;
  for (const s of [-1, 1]) {
    const z = 28 * s;
    jerseyBarrier(w, { x: -6 + s * 2, z: z - s * 3, rotY: 0.04 * s, len: 2.6 });
    jerseyBarrier(w, { x: -3.2 + s * 2, z: z - s * 3, rotY: -0.02, len: 2.6 });
    jerseyBarrier(w, { x: 9.0, z: z + s * 1.5, rotY: Math.PI / 2 + 0.03, len: 2.6 });
    sandbagWall(w, { a: [1.0, z], b: [4.6, z] , rows: 4 });
    scatter(w, 'crate_wood_lg', { x0: -22, z0: z - 4, x1: 26, z1: z + 4, count: 8 });
    scatter(w, 'barrel', { x0: -22, z0: z - 4, x1: 26, z1: z + 4, count: 7 });
    scatter(w, 'tyre', { x0: -22, z0: z - 4, x1: 26, z1: z + 4, count: 6 });
    scatter(w, 'pallet', { x0: -22, z0: z - 4, x1: 26, z1: z + 4, count: 5 });
    scatter(w, 'cinder_block', { x0: -22, z0: z - 4, x1: 26, z1: z + 4, count: 9 });
    weeds(w, { x0: -26, z0: z - 5, x1: 30, z1: z + 5, count: 40 });
    for (const x of [-18, 24]) {
      w.props.place('street_lamp', xform([x, 0, z], rng.range(0, 6.28)));
      w.light([x, 5.0, z + 1.0], 0xffd8a8, 2.0, 12, 'point', { role: 'streetlight' });
    }
  }
  pickup(w, { x: -18.5, z: 27.0, rotY: 1.15, tint: [0.62, 0.66, 0.6] });
  sedan(w, { x: 21.0, z: -27.5, rotY: -0.65, tint: [0.7, 0.68, 0.64], flat: true });
}

/* ------------------------------------------------------------------ *
 * system
 * ------------------------------------------------------------------ */

export function createLevelSystem() {
  const root = new THREE.Group();
  root.name = 'level';
  let world = null;
  let meshes = [];

  return {
    name: 'level',
    priority: 20,

    init(ctx) {
      const rng = makeRng(0x4b48414c); // "KHAL"
      world = new World(rng);
      defineProps(world);

      buildGround(world);
      buildAlley(world);
      buildSquare(world);
      buildShop(world);
      buildRing(world);
      buildSpawns(world);
      buildSkyline(world);
      const sil = buildSilhouette(world);
      buildLightMarkers(world);

      const resolve = makeResolver(ctx);
      const statics = world.batch.build(root, resolve, 'khalij');
      const props = world.props.build(root, resolve);
      const collision = world.buildCollision(root);
      meshes = [...statics, ...props];

      ctx.scene.add(root);

      const eye = 1.65;
      const spawnPoints = [
        new THREE.Vector3(-6.5, eye, -29.5), new THREE.Vector3(-1.0, eye, -30.5),
        new THREE.Vector3(4.5, eye, -29.5), new THREE.Vector3(-14.0, eye, -28.5),
        new THREE.Vector3(12.5, eye, -28.5), new THREE.Vector3(-22.8, eye, -29.5),
      ];
      const enemySpawns = [
        new THREE.Vector3(-5.5, eye, 29.5), new THREE.Vector3(0.5, eye, 30.5),
        new THREE.Vector3(6.0, eye, 29.5), new THREE.Vector3(-15.0, eye, 28.5),
        new THREE.Vector3(13.5, eye, 28.5), new THREE.Vector3(-22.8, eye, 29.5),
        new THREE.Vector3(19.0, eye, 10.0), new THREE.Vector3(-16.8, eye + BLOCKB.roof, 8.0),
      ];

      const navBounds = new THREE.Box3(
        new THREE.Vector3(PLAY.x0 - 2, -1, PLAY.z0 - 2),
        new THREE.Vector3(PLAY.x1 + 2, 12, PLAY.z1 + 2));

      ctx.level = {
        root,
        spawnPoints,
        enemySpawns,
        navBounds,
        lightHints: world.lightHints,
        collision,
        surfaceAt: (p) => world.surfaceAt(p),
        /**
         * N2 acceptance test. Raycasts straight down from a 5×5 grid over every
         * awning / parasol / stall canopy / souk cloth panel in the map and
         * reports how many have nothing holding them up. Must be 0.
         */
        verifyOverheads: () => verifyOverheads(root, world.overheads),
        // extras other teams may find useful
        bounds: navBounds,
        name: 'BLACKOUT: KHALIJ',
        lanes: {
          left: { x0: ALLEY.x0, x1: ALLEY.x1 },
          centre: { x0: SQUARE.x0, x1: SQUARE.x1 },
          right: { x0: SHOP.x0, x1: SHOP.x1 },
        },
        /**
         * F1b hand-off. The map's long axis is Z; the ring rows over the centre
         * lane were lowered to 2 storeys (≈7.7 m to the coping at z = ±34) so a
         * sun anywhere in this envelope puts direct light on the square's
         * paving while the souk house (8.2 m at x = 15) keeps a held shadow on
         * the east side. Below 30° the ring starts to shade the square again.
         */
        sunEnvelope: {
          elevationDeg: [32, 42],
          azimuthAxis: 'z',
          azimuthToleranceDeg: 35,
          litGround: [new THREE.Vector3(0.5, 0.0, -1.0), new THREE.Vector3(-2.0, 0.0, 11.0)],
          heldShadow: [new THREE.Vector3(12.6, 0.0, -6.0), new THREE.Vector3(-9.6, 0.0, -7.4)],
        },
        /**
         * Ground-collision self-test (F2).
         *
         * Two rays per spawn:
         *   `high` — from `up` metres above the spawn point, the literal check
         *            asked for. It reports the *ceiling* for indoor spawns,
         *            which is correct behaviour, not a missing floor.
         *   `feet` — from 0.6 m above the capsule's feet. This is the one that
         *            decides pass/fail: the floor must be within 0.6 m of where
         *            the spawn says the feet are.
         */
        verifyGround(physics, up = 3, far = 40) {
          const eye = 1.65;
          const dir = new THREE.Vector3(0, -1, 0);
          const mask = physics?.MASK?.WORLD ?? 1;
          const cast = (x, y, z, d) => physics?.raycast?.(new THREE.Vector3(x, y, z), dir, d, mask) || null;
          const out = [];
          for (const [kind, list] of [['player', spawnPoints], ['enemy', enemySpawns]]) {
            list.forEach((p, i) => {
              const feetY = p.y - eye;
              const high = cast(p.x, p.y + up, p.z, far);
              const low = cast(p.x, feetY + 0.6, p.z, far);
              out.push({
                kind, i, x: p.x, y: p.y, z: p.z,
                highHit: !!high,
                highY: high ? +high.point.y.toFixed(3) : null,
                highDist: high ? +high.distance.toFixed(3) : null,
                floorHit: !!low,
                floorY: low ? +low.point.y.toFixed(3) : null,
                surface: low ? low.surface : null,
                gapUnderFeet: low ? +(feetY - low.point.y).toFixed(3) : null,
                ok: !!low && Math.abs(feetY - low.point.y) <= 0.6,
              });
            });
          }
          return out;
        },
      };

      this._toRegister = collision;

      /*
       * N2 — floating awnings. Run the support check on every boot and shout if
       * it is non-zero. This is the eye-independent evidence the reviewer asked
       * for; the last time this defect was "fixed" it was still in the frame.
       */
      try {
        const t0 = (globalThis.performance || Date).now();
        const sup = verifyOverheads(root, world.overheads);
        const ms = Math.round((globalThis.performance || Date).now() - t0);
        if (sup.failed) {
          console.error(`[level] UNSUPPORTED OVERHEADS: ${sup.failed} of ${sup.total} — `
            + sup.details.filter((d) => !d.ok).map((d) => `${d.kind}@${d.at.join(',')} gap ${d.worstGap}`).join(' | '));
        } else {
          console.log(`[level] overhead support check: ${sup.failed} unsupported of ${sup.total} `
            + `awning/parasol/canopy instances (worst gap to a support ${sup.worst} m, ${ms} ms)`);
        }
      } catch (err) {
        console.error('[level] overhead support check failed to run', err);
      }

      const drawCalls = meshes.length;
      console.log(`[level] BLACKOUT: KHALIJ — ${drawCalls} draw calls `
        + `(${statics.length} merged static, ${props.length} instanced), `
        + `${world.batch.stats.pieces} authored pieces, `
        + `${(world.batch.stats.verts / 1000).toFixed(0)}k verts, `
        + `${collision.length} collision proxies, ${world.lightHints.length} light hints, `
        + `silhouette: ${sil.towers} lattice towers, ${sil.anchors} cable anchors, ${sil.spans} spans`);

      registerShots(ctx);
    },

    /**
     * Physics initialises after us (priority 25 vs 20), so ctx.physics does not
     * exist during our init. Hand the collision proxies over on the first frame
     * instead — physics is also free to read ctx.level.collision itself.
     */
    update(dt, ctx) {
      // restore the viewmodel a few frames after one of our shots grabbed it,
      // so shots owned by other teams are never affected
      if (hideViewmodelFrames > 0 && --hideViewmodelFrames === 0) {
        const vm = ctx.weapons && ctx.weapons.viewmodelRoot;
        if (vm) vm.visible = true;
      }
      if (!this._toRegister) return;
      const api = ctx.physics;
      if (!api || !api.addStatic) { this._toRegister = null; return; }
      try {
        for (const m of this._toRegister) api.addStatic(m, { mask: 1 });
        api.rebuild?.();
      } catch (err) {
        console.warn('[level] physics.addStatic failed; physics can read ctx.level.collision', err);
      }
      this._toRegister = null;
    },

    dispose() {
      root.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
      });
      root.removeFromParent();
    },
  };
}

/* ------------------------------------------------------------------ *
 * shots
 * ------------------------------------------------------------------ */

const _stageDir = new THREE.Vector3();
const _stageFeet = new THREE.Vector3();
/** Frames to keep the viewmodel hidden after a level shot is staged. */
let hideViewmodelFrames = 0;

/**
 * Park the camera for a shot. The player system owns the main camera every
 * frame, so a shot has to move the *player*, not just ctx.camera — otherwise the
 * settle frames drag the view back to wherever the capsule actually is and
 * smear the result with motion blur.
 */
function stage(c, from, at, fov) {
  look(c, from, at, fov);
  const sys = c.get?.('player');
  _stageDir.set(at[0] - from[0], at[1] - from[1], at[2] - from[2]).normalize();
  const yaw = Math.atan2(-_stageDir.x, -_stageDir.z);
  const pitch = Math.asin(THREE.MathUtils.clamp(_stageDir.y, -1, 1));
  const eyeH = c.player?.eyeHeight ?? 1.65;
  _stageFeet.set(from[0], from[1] - eyeH, from[2]);

  if (sys) {
    // place() teleports the capsule as well, so the rig has nothing to snap to.
    if (sys.place) sys.place(_stageFeet, yaw, pitch);
    else if (sys.rig?.reset) sys.rig.reset(yaw, pitch);
    sys.freeze?.(true);
    const rig = sys.rig;
    if (rig) {
      if (rig.eye) rig.eye.set(from[0], from[1], from[2]);
      rig.fov = fov;
      rig.applyTo?.(c);
    }
  }
  // re-assert the exact framing after the rig has had its say
  look(c, from, at, fov);
  if (c.player) {
    c.player.position.set(from[0], from[1], from[2]);
    c.player.ads = 0;
  }
  const vm = c.weapons && c.weapons.viewmodelRoot;
  if (vm) { vm.visible = false; hideViewmodelFrames = 14; }
}

function registerShots(ctx) {
  registerShot('level_overview',
    (c) => stage(c, [37, 51, 55], [-4.0, 1.0, -2.0], 36),
    { desc: 'BLACKOUT: KHALIJ — full three-lane layout from the south-east', settle: 10 });

  registerShot('level_square',
    (c) => stage(c, [0.4, 1.66, 13.2], [-0.6, 2.15, -24.0], 74),
    { desc: 'eye level in the market square, looking down the centre lane', settle: 10 });

  registerShot('level_alley',
    (c) => stage(c, [-22.6, 1.66, 8.6], [-23.2, 2.6, -26.0], 78),
    { desc: 'eye level in the alley — arches, stairs, balcony walkway', settle: 10 });

  registerShot('level_interior',
    (c) => stage(c, [20.2, 1.60, 0.4], [12.5, 1.55, -6.6], 74),
    { desc: 'inside the souk house, looking out the blown-in shopfront', settle: 10 });

  registerShot('level_rooftop',
    (c) => stage(c, [-14.9, 5.95, -0.75], [16.0, 3.4, -6.0], 76),
    { desc: 'block B roof terrace, through the blown parapet across the square', settle: 10 });

  registerShot('level_detail',
    (c) => stage(c, [-10.8, 1.05, -10.9], [-13.1, 0.20, -13.8], 50),
    { desc: '1.5 m from the alley wall base — bevels, rubble, weeds, wear', settle: 8 });

  void ctx;
}
